import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { KittensIllustration, PawIllustration } from "./wizardIllustrations";
import { Banknote, CreditCard, FileText, Landmark, Printer, Wallet } from "lucide-react";
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useList,
  usePermissions,
  useTranslate,
} from "@refinedev/core";
import { Button, Field, Modal, SelectField, TextareaField, ToggleField } from "@fawkes/ui";
import { ComboBox } from "./ComboBox";
import { StepIntro, WizardModal } from "./WizardModal";
import { PaymentStep } from "../components/PaymentStep";
import { betalingOordeel } from "../components/betalingRegels";
import { afgerondeEuro, kortingRegelTeHoog, kortingTotaal, kortingVanRegel, regelNaKorting }
  from "../components/KortingVeld";
import { AnimalSubwizard, AnimalThumb, animalRowText, type NewAnimalResult, type SubwizardAnimal } from "./AnimalSubwizard";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { uploadIntakeAnimalPhoto } from "../api/intakePhoto";
import { ProductTariefPicker, type GekozenProductRegel } from "../components/ProductTariefPicker";
import { KostenregelBtw, kostenregelZonderBtw, useBtwPercentages } from "../components/BtwKeuze";
import { FoundLocationMap } from "../components/FoundLocationMap";
import { resolveGemeenteId, useFoundLocationGeocoding } from "../components/useFoundLocationGeocoding";
import { useVestigingMapCenter } from "../components/useVestigingMapCenter";
import { SignaturePad } from "../components/SignaturePad";
import { addressLine, type KlantDetail } from "../components/RelationContactPanel";
import { OwnerConfirmCard, type KnownOwnerView } from "../components/OwnerConfirmCard";
import { RelationPicker } from "../components/RelationPicker";
import { downloadWizardDocumentPdf, fetchWizardDocumentPreview } from "../api/wizardDocument";
import { formatDate, formatEuro, todayIso } from "../i18n/format";

// wizard-redesign: the stepper phases are PER FLOW. The generic 5 labels ("Herkomst/Plaatsing") don't
// fit every intake type — a surrender has no "Herkomst" or "Plaatsing". So each step key maps to a
// flow-specific phase label, and the actual `phases` array is derived from the steps that are present
// (see the component) so absent steps (no vindersverklaring/chip) never leave an empty phase. The
// mapping stays monotonic along the real step order, so the stepper only ever moves forward.
function intakePhaseLabel(key: string, option: string): string {
  switch (key) {
    case "count":
      return "Aantal dieren";
    case "animals":
    case "type":
      return "Diergegevens";
    case "summary":
      return "Controle & afronden";
    case "ownerConfirm":
    case "relationFind":
      // A stray has no owner — the same search step looks for the finder instead.
      return option === "STRAY" ? "Vinder" : "Eigenaar";
    case "exchangeSource":
      return "Herkomst asiel";
    case "strayAmbulance":
      // Groups with the found location under one Herkomst phase — two steps, one stepper dot.
      return "Herkomst";
    case "terms": // stray vindersverklaring
      return "Verklaring";
    case "surrenderCosts":
    case "surrenderTerms":
    case "surrenderPayment":
    case "returnRestitution":
    case "returnTerms":
    case "returnPayment":
    case "chip":
      return "Afhandeling";
    case "details":
      if (option === "STRAY") return "Herkomst";
      if (option === "SURRENDER" || option === "RETURN") return "Eigenaar";
      return "Gegevens"; // BIRTH / generic
    default:
      return "Diergegevens";
  }
}

// ── types ────────────────────────────────────────────────────────────────────────────────────────

interface OptionRow {
  id: number;
  label: string;
  color: string | null;
}
/** A municipality plus its own towns — feeds the Vindplaats step's coupled Gemeente/Woonplaats pair. */
interface GemeenteRow {
  id: number;
  label: string;
  woonplaatsen: string[];
}
interface IntakeSoort {
  id: number;
  label: string;
  option: string; // STRAY | SURRENDER | RETURN | BIRTH | EXCHANGE | DEFAULT
  // Curated allow-list type that is never selectable today (Tijdelijke opvang / Overig) — shown
  // greyed, mirroring the Jmix "tijdelijk niet beschikbaar" cards.
  alwaysDisabled: boolean;
}
// An ASIEL-eligible shelter for the EXCHANGE Herkomst-asiel step
// (GET intake/reference/exchange-shelters — ExchangeShelterOption).
interface ExchangeShelterRow {
  id: number;
  naam: string | null;
  email: string | null;
  telefoon: string | null;
  woonplaats: string | null;
  adres: string | null;
}
interface NewAnimalDraft {
  naam: string;
  diersoortId: number;
  geslacht: string | null;
  geboortedatum: string | null;
  // Full Jmix AnimalDetailsFragment field set (parity-audit slice 3) — matches NewAnimalSpec.
  chipPasnummer: string | null;
  rasId: number | null;
  kruising: boolean;
  vachtId: number | null;
  kleurId: number | null;
  gecastreerd: boolean;
}
interface SelectedAnimal {
  // For an existing animal, the real dier_id; for a freshly-entered one, a synthetic negative id
  // (used only as a React key / dedup token — the payload carries `newAnimal` instead).
  id: number;
  label: string;
  // Species name, captured so the "Wilde kat" type can be gated to cats-only (Jmix isCatSpecies).
  species: string | null;
  // Jmix summary-row facts (chip | naam | soort | ras | geslacht | kleur | vacht + 56px thumbnail).
  naam: string;
  chip: string | null;
  ras: string | null;
  geslacht: string | null;
  kleur: string | null;
  vacht: string | null;
  fotoUrl: string | null;
  newAnimal?: NewAnimalDraft;
  // The sub-wizard's captured photo (upload/camera) — React state only, NEVER serialized into the
  // WizardData draft JSON; uploaded best-effort after /complete returned the created animal ids.
  photo?: File | null;
}

// Tariff auto-proposal (POST /api/tarief-voorstel) — React parity for the Jmix "Tarief voorstellen"
// affordance in the surrender costs step. Explicit status so the UI never guesses from a null amount.
interface TariefVoorstelResponse {
  status: "MATCHED" | "NO_MATCH" | "CONFLICT" | "MANUAL_QUOTE";
  regelNaam: string | null;
  omschrijving: string | null;
  bedrag: number | null;
  btwPercentage?: number | null;
  conflictRegels: string[];
  toelichting: string;
}

interface IntakeEditView {
  verblijfId: number;
  animalId: number;
  animalLabel: string | null;
  asielOpnameSoortId: number | null;
  opnameDatum: string | null;
  klantInId: number | null;
  klantInLabel: string | null;
  animalAmbulanceInId: number | null;
  animalAmbulanceInRideNumber: string | null;
  gevondenGemeenteId: number | null;
  strayFoundLocationStreet: string | null;
  strayFoundLocationHouseNumber: string | null;
  strayFoundLocationHouseNumberAddition: string | null;
  strayFoundLocationPostalCode: string | null;
  strayFoundLocationCity: string | null;
  strayFoundLocationRemark: string | null;
  strayFoundLocationLatitude: number | null;
  strayFoundLocationLongitude: number | null;
  strayDumped: boolean;
  surrenderReasonCode: string | null;
  surrenderReasonRemark: string | null;
  returnReason: string | null;
  returnRemark: string | null;
  birthMotherAnimalId: number | null;
  birthMotherAnimalName: string | null;
  birthRemark: string | null;
  version: number | null;
}
type Notice = null | "conflict" | "invalid" | "error";

// ── PetBase chip-registration capture (Epic 3) ─────────────────────────────────────────────────────
// One per-animal draft for the chip step; PATCHed into the WizardData JSON under `chipCaptures` and
// enqueued server-side on /complete (the actual PetBase submit is async — no immediate verdict here).
interface ChipCaptureDraft {
  aanvoerType: string;
  eventDate: string;
  chipNumber: string;
  countryOfOrigin: string;
  motherChipNumber: string;
  newChipInsertOn: string;
  chipReadDate: string;
  chipperCibgNumber: string;
}
interface ChipperOption {
  cibgNumber: string;
  displayName: string;
}
interface ChipIdentifiersResponse {
  configured: boolean;
  reachable: boolean;
  identifiers: ChipperOption[];
}
// The known owner shown on the surrender owner-confirmation step (GET …/known-owner — KnownOwnerResponse).
// The read-only Dier facts shown on the chip step (GET /api/animals/{id} — AnimalDetailResponse subset).
interface ChipAnimalDetail {
  naam: string | null;
  geslacht: string | null;
  gecastreerd: boolean;
  diersoort: string | null;
  ras: string | null;
  kruising: boolean;
  vacht: string | null;
  kleur: string | null;
  paspoortNummer: string | null;
  kenmerken: string | null;
  geboortedatum: string | null;
}
// The four PetBase aanvoer-meldingstypes (faithful to the Jmix AanvoerType vocabulary).
const AANVOER_TYPES = [
  "AANVOER_NIEUWE_EIGENAAR",
  "AANVOER_NIEUWE_CHIP",
  "AANVOER_GEBOORTE",
  "AANVOER_IMPORT",
] as const;
const AANVOER_GEBOORTE = "AANVOER_GEBOORTE";
const AANVOER_NIEUWE_CHIP = "AANVOER_NIEUWE_CHIP";

function isExactly15Digits(value: string): boolean {
  return /^\d{15}$/.test(value.replace(/\s/g, ""));
}

/** Verbatim-Dutch validation of one chip capture (FR14); returns the i18n keys of any errors. */
function chipCaptureErrors(d: ChipCaptureDraft | undefined): string[] {
  const errors: string[] = [];
  if (!d) return ["inboeken.chip.validation.aanvoerType"];
  if (!d.aanvoerType) errors.push("inboeken.chip.validation.aanvoerType");
  if (!d.eventDate) errors.push("inboeken.chip.validation.eventDate");
  if (!isExactly15Digits(d.chipNumber || "")) errors.push("inboeken.chip.validation.chipFormat");
  if (!d.countryOfOrigin.trim()) errors.push("inboeken.chip.validation.countryOfOrigin");
  if (!d.chipperCibgNumber) errors.push("inboeken.chip.validation.chipper");
  if (!d.chipReadDate) errors.push("inboeken.chip.validation.chipReadDate");
  if (d.aanvoerType === AANVOER_GEBOORTE) {
    // Jmix distinguishes "required" (blank) from "must be 15 digits" (present but malformed).
    const motherChip = (d.motherChipNumber || "").trim();
    if (!motherChip) {
      errors.push("inboeken.chip.validation.motherChip");
    } else if (!isExactly15Digits(motherChip)) {
      errors.push("inboeken.chip.validation.motherChipFormat");
    }
  }
  if (d.aanvoerType === AANVOER_NIEUWE_CHIP && !d.newChipInsertOn) {
    errors.push("inboeken.chip.validation.chipInsertDate");
  }
  return errors;
}

/**
 * Read-only Dier facts on the chip step (visual parity with the Jmix ChipregistratieInboekFragment Dier
 * block). Fetched per chipped animal from {@code GET /api/animals/{id}}; the registration payload is still
 * built server-side from {@code Dier}, so this is display-only. Its own component so the per-animal fetch
 * is one hook per card (no hooks-in-a-loop).
 */
function ChipAnimalReadonly({ animalId, t }: { animalId: number; t: (k: string) => string }) {
  const { data } = useCustom({ url: `animals/${animalId}`, method: "get" });
  const d = (data?.data as ChipAnimalDetail | undefined) ?? null;
  const yesNo = (v: boolean | undefined) => t(v ? "common.yes" : "common.no");
  return (
    <div className="fawkes-chipreg__readonly">
      <SummaryRow label={t("inboeken.chip.field.name")} value={d?.naam ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.gender")} value={d?.geslacht ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.species")} value={d?.diersoort ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.breed")} value={d?.ras ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.coat")} value={d?.vacht ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.color")} value={d?.kleur ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.birthDate")} value={d?.geboortedatum ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.passport")} value={d?.paspoortNummer ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.features")} value={d?.kenmerken ?? "—"} />
      <SummaryRow label={t("inboeken.chip.field.castrated")} value={yesNo(d?.gecastreerd)} />
      <SummaryRow label={t("inboeken.chip.field.crossBreed")} value={yesNo(d?.kruising)} />
    </div>
  );
}

const STRAY = "STRAY";
const SURRENDER = "SURRENDER";
const RETURN = "RETURN";
const BIRTH = "BIRTH";
const EXCHANGE = "EXCHANGE";

// Max animals per intake (Jmix SelectAnimalCountFragment.MAXIMUM_ANIMAL_COUNT). The 2-as-minimum for
// the explicit "multiple" path doesn't apply here — React's multi-select treats a single pick as the
// "one animal" path — so only the upper bound is enforced.
const MAX_ANIMAL_COUNT = 10;

// Fixed "Reden afstand" vocabulary — business-specified, stored literally in
// asiel_verblijf.reden_afstand (NOT the asiel_reden_afstand reference table). Mirrors the Jmix
// SurrenderReasonFragment.SURRENDER_REASONS list verbatim.
const SURRENDER_REASONS = ["Particulier", "Sociaal", "Preventie", "LID-afstand", "Rijksafstand"];

/** The payment shorthands on the Betaling step — which amount rows the operator means to use. */
type SurrenderPayMethod = "pin" | "cash" | "combi" | "invoice";
const SURRENDER_PAY_METHODS: { key: SurrenderPayMethod; labelKey: string; icon: ReactNode }[] = [
  { key: "pin", labelKey: "inboeken.payment.pin", icon: <CreditCard size={18} aria-hidden="true" /> },
  { key: "cash", labelKey: "inboeken.payment.cash", icon: <Banknote size={18} aria-hidden="true" /> },
  { key: "combi", labelKey: "inboeken.payment.combi", icon: <Wallet size={18} aria-hidden="true" /> },
  { key: "invoice", labelKey: "inboeken.payment.invoice", icon: <FileText size={18} aria-hidden="true" /> },
];
const SURRENDER_PAY_ROWS: { key: "pin" | "cash" | "later"; labelKey: string; icon: ReactNode }[] = [
  { key: "pin", labelKey: "inboeken.payment.pin", icon: <CreditCard size={16} aria-hidden="true" /> },
  { key: "cash", labelKey: "inboeken.payment.cash", icon: <Banknote size={16} aria-hidden="true" /> },
  { key: "later", labelKey: "inboeken.payment.invoiceOpen", icon: <FileText size={16} aria-hidden="true" /> },
];

/** A total as a plain editable amount string ("50.00") for prefilling the amount inputs. */
function formatAmount(value: number): string {
  return value > 0 ? value.toFixed(2) : "";
}

// Fixed "Reden retour" vocabulary on the restitution step — the ReturnRestitutionFragment.reason.*
// list verbatim (parity-audit §3 row 14), stored literally in asiel_verblijf.retour_reden (like the
// surrender reasons above).
const RETURN_REASONS = [
  "Gedragsproblemen",
  "Geen goede match",
  "Allergieën",
  "Onrealistische verwachtingen",
  "Zindelijkheidsproblemen",
  "Gezondheidsproblemen",
  "Mislukte koppeling",
];

// The previous-adoption overview behind the RETURN restitution step
// (GET intake/reference/animals/{id}/return-restitution — ReturnRestitutionResponse).
interface ReturnRestitutionView {
  animalId: number;
  placedOn: string | null;
  invoiceKlantId: number | null;
  invoiceKlantLabel: string | null;
  ownerIban: string | null;
  lines: { description: string | null; amount: number }[];
  totalPaid: number;
  maxRefund: number;
}

// The three combinable payout methods (Jmix ReturnPaymentFragment rows, in its order). Module-level:
// the step defs render before the component body finishes (a const inside would hit the TDZ).
type ReturnPayMethod = "transfer" | "pin" | "cash";
const RETURN_PAY_METHODS: { key: ReturnPayMethod; labelKey: string; icon: ReactNode }[] = [
  { key: "transfer", labelKey: "inboeken.returnPayment.transfer", icon: <Landmark size={16} aria-hidden="true" /> },
  { key: "pin", labelKey: "inboeken.returnPayment.pin", icon: <CreditCard size={16} aria-hidden="true" /> },
  { key: "cash", labelKey: "inboeken.returnPayment.cash", icon: <Banknote size={16} aria-hidden="true" /> },
];

/** De tegels boven de bedragregels: de drie methodes plus "Combinatie" om zelf te verdelen. */
type ReturnPayTile = ReturnPayMethod | "combi";
const RETURN_PAY_TILES: { key: ReturnPayTile; labelKey: string; icon: ReactNode }[] = [
  { key: "transfer", labelKey: "inboeken.returnPayment.transfer", icon: <Landmark size={18} aria-hidden="true" /> },
  { key: "pin", labelKey: "inboeken.returnPayment.pin", icon: <CreditCard size={18} aria-hidden="true" /> },
  { key: "cash", labelKey: "inboeken.returnPayment.cash", icon: <Banknote size={18} aria-hidden="true" /> },
  { key: "combi", labelKey: "inboeken.payment.combi", icon: <Wallet size={18} aria-hidden="true" /> },
];

/** Whole days between two ISO dates, clamped to ≥ 0 (Jmix ChronoUnit.DAYS.between — no +1 here). */
function daysBetween(fromIso: string | null, toIso: string | null): number | null {
  if (!fromIso || !toIso) return null;
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 86_400_000));
}

// ── type-card presentation (Phase 9) ───────────────────────────────────────────────────────────────
// Per-intake-type icon + description, mirroring the Jmix ShelterIntakeTypeCardFragment (resolveTypeKey
// by option + name; .info descriptions). Icons are the line-art SVGs ported from the Jmix intake assets.

function typeKey(soort: IntakeSoort): string {
  if (soort.option === BIRTH) return "birth";
  const n = soort.label.toLowerCase().trim();
  if (n.includes("zwer")) return "stray";
  if (n.includes("afstand")) return "surrender";
  if (n.includes("retour") || n.includes("return")) return "return";
  if (n.includes("uitwiss") || n.includes("exchange")) return "exchange";
  if (n.includes("geboort") || n.includes("birth")) return "birth";
  // "TNRC" is what the wilde-kat opnamesoort is called now; the old name is kept as a match so an
  // instance that hasn't been renamed (or an older row) still gets the cat icon and the cats-only gate.
  if (n.includes("tnrc") || n.includes("wild")) return "wildcat";
  if (n.includes("tijdelijke opvang") || n.includes("opvang overeenkomst") || n.includes("prevent")) return "preventie";
  if (n.includes("overig") || n.includes("other")) return "other";
  if (soort.option === STRAY) return "stray";
  if (soort.option === SURRENDER) return "surrender";
  return "other";
}

// The polished Jmix intake type icons (the "_custom" set — house+arrow, paw+pin, puppy+paw, …),
// copied verbatim from the Jmix resources (META-INF/resources/images/intake). Tijdelijke opvang has
// no custom PNG in that set, so it keeps the line-art SVG.
const TYPE_ICON: Record<string, string> = {
  stray: "/intake/icon_zwerver_custom.png",
  surrender: "/intake/icon_afstand_custom.png",
  return: "/intake/icon_retour_custom.png",
  exchange: "/intake/icon_uitwisseling_custom.png",
  birth: "/intake/icon_geboorte_custom.png",
  wildcat: "/intake/icon_wildekat_custom.png",
  preventie: "/intake/icon_tijdelijkeopvang.svg",
  other: "/intake/icon_overig_custom.png",
};

// Only these types carry a Jmix .info description; the rest show the title alone.
const TYPE_DESC_KEY: Record<string, string> = {
  stray: "inboeken.type.stray.info",
  surrender: "inboeken.type.surrender.info",
  return: "inboeken.type.return.info",
  exchange: "inboeken.type.exchange.info",
  birth: "inboeken.type.birth.info",
};

function isCat(species: string | null): boolean {
  return (species ?? "").toLowerCase().includes("kat");
}

/**
 * Structural IBAN check (Jmix ReturnPaymentFragment: "Voer een geldig IBAN-nummer in."): country
 * prefix + check digits + ISO 7064 mod-97 == 1, spaces ignored. Length is validated loosely
 * (15–34) — the mod-97 rearrangement catches virtually every typo.
 */
function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const value = ch >= "A" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of value) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** Whole months from an ISO birth date to today, clamped to ≥ 0; null when the date is unknown/invalid. */
function monthsSince(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const birth = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months -= 1;
  return Math.max(0, months);
}

// ── small data hooks ───────────────────────────────────────────────────────────────────────────

function useFeed<T>(url: string, enabled = true): T[] {
  const { data } = useCustom({ url, method: "get", queryOptions: { enabled } });
  return (data?.data as T[] | undefined) ?? [];
}

function noticeFor(err: unknown): Notice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 422 ? "invalid" : "error";
}

/** Parse a euro amount from a free-text field (accepts a comma or dot decimal); null when blank/NaN. */
function parseAmount(value: string): number | null {
  const s = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// ── the wizard ───────────────────────────────────────────────────────────────────────────────────

/**
 * Inboeken (intake) wizard — a full-page screen that takes one or more animals into the shelter,
 * creating an {@code asiel_verblijf} residency per animal via {@code POST /api/intake}. Steps follow
 * the Jmix wizard order (wizard-jmix-parity): Aantal → Dieren → Soort → Gegevens → … → Controle,
 * rendered as Jmix-style step cards (completed steps collapse to summary bars — see StepWizard).
 * Changing the intake type clears the type-specific fields (branch
 * clearing). Supports existing-animal pick and new-{@code Dier} creation, the stray ambulance step,
 * type-specific fields (found-location / surrender reason / return reason / birth mother + gemeente),
 * an edit mode (launched with {@code editVerblijfId}), and a generic WizardData draft. The relation
 * picker can also create a new relation inline (via the relatie module's own {@code POST /api/relaties},
 * gated on {@code ROLE_RELATIE_EDIT}). Chip/PetBase capture, surrender costs/payment/terms, the
 * return restitution/payout steps and the found-location map are all built; the remaining gaps are
 * tracked in _bmad-output/planning-artifacts/wizard-parity-audit-2026-07-04.md.
 */
export function InboekenWizard({
  preselectedAnimalId,
  editVerblijfId,
  linkedExchangeId,
  linkedExchangeSource,
  onDone,
  onCancel,
  onExpand,
}: {
  preselectedAnimalId?: string | null;
  editVerblijfId?: string | null;
  // Receiving-side intake of an exchanged animal (Epic 3 Story 10): when launched from an accepted
  // internal exchange, completing this intake also completes that exchange (server-side).
  linkedExchangeId?: string | null;
  // The linked exchange's source shelter (klant), passed by the Uitwisselingen inbox launcher —
  // preset as the brenger so the Herkomst-asiel step can be dropped (Jmix
  // ShelterIntakePrepopulationService: bringer = ShelterExchange.sourceKlant).
  linkedExchangeSource?: { id: number | null; label: string | null } | null;
  // Completion navigation (audit §3 row 23): the created animal ids from the /complete response, so
  // the host can open the animal detail (one animal) or the aanwezigheidsoverzicht (several) — the
  // Jmix post-finish navigation. Absent on cancel/fallback (host keeps its previous behavior).
  onDone: (animalIds?: number[]) => void;
  onCancel: () => void;
  // wizard-dialog-mode: present when hosted as a dialog — switches to the full-page view (the host
  // keeps this component mounted, so in-progress entries and the open draft survive the switch).
  onExpand?: () => void;
}) {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const locale = getLocale() ?? "nl";
  // The "Nieuwe relatie" affordance is shown only when the operator also holds the relatie module's
  // own create authority (ROLE_RELATIE_EDIT) — we reuse POST /api/relaties rather than backdooring a
  // create path under the intake authority, so relation governance stays in the relatie module.
  const { data: permissions } = usePermissions<string[]>();
  const canCreateRelatie = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  const editMode = editVerblijfId != null && editVerblijfId !== "";
  // wizard-jmix-parity: `step` = the expanded card, `furthest` = the highest card reached — every
  // other reached step renders as a collapsed summary bar (Jmix StepCard). A locked launch (edit
  // mode / preselected animal / exchange intake) starts past the count+animals steps, which show
  // as pre-completed bars.
  const startLocked = editMode || (preselectedAnimalId != null && preselectedAnimalId !== "");
  const [step, setStep] = useState(startLocked ? 2 : 0);
  // De vraag die openstaat terwijl iemand hem beantwoordt: wat er daarna moet gebeuren.
  const [restantVraag, setRestantVraag] = useState<null | (() => void)>(null);
  const [restantBedrag, setRestantBedrag] = useState(0);
  const [furthest, setFurthest] = useState(startLocked ? 2 : 0);
  // Jmix SelectAnimalCountFragment ("Hoeveel dieren wil je inboeken?"): Één dier / Meerdere dieren
  // with a 2–10 count. null = not chosen yet (gates the count step).
  const [multi, setMulti] = useState<boolean | null>(startLocked ? false : null);
  const [countInput, setCountInput] = useState("2");
  const [notice, setNotice] = useState<Notice>(null);
  const [version, setVersion] = useState<number | null>(null);

  // Step 1 — type
  const soorten = useFeed<IntakeSoort>("intake/reference/opnamesoorten");
  const [soortId, setSoortId] = useState<string>("");
  const option = useMemo(
    () => soorten.find((s) => String(s.id) === soortId)?.option ?? "DEFAULT",
    [soorten, soortId],
  );

  // Step 2 — animals (existing). The list backs both the preselect-label lookup and the search.
  const { data: animalData } = useList<SubwizardAnimal>({
    resource: "animals",
    pagination: { mode: "off" },
  });
  const animals = animalData?.data ?? [];
  const [selected, setSelected] = useState<SelectedAnimal[]>([]);
  // The animal is fixed (not searchable) when launched for a specific animal or in edit mode.
  const locked = editMode || (preselectedAnimalId != null && preselectedAnimalId !== "");
  const [newKeySeq, setNewKeySeq] = useState(-1);

  // Jmix shows a pending captured photo in the add-animals rows and the summary before completion
  // (ShelterIntakeAddAnimalsFragment/SurrenderShelterIntakeSummaryFragment resolveAnimalPhotoSrc
  // prefer the in-memory pending photo). The React photo only uploads post-complete, so mirror
  // that by deriving an object URL per selected entry that carries a captured File.
  const [pendingPhotoUrls, setPendingPhotoUrls] = useState<ReadonlyMap<number, string>>(new Map());
  useEffect(() => {
    // jsdom has no createObjectURL — rows then keep the camera placeholder in tests
    if (typeof URL.createObjectURL !== "function") return;
    const next = new Map<number, string>();
    for (const a of selected) {
      if (a.photo) next.set(a.id, URL.createObjectURL(a.photo));
    }
    setPendingPhotoUrls(next);
    return () => {
      if (typeof URL.revokeObjectURL !== "function") return;
      next.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selected]);
  // Authenticated uploaded photos for the selected animals (the plain fotoUrl is null for
  // shelter-uploaded animals, so the recap rows fell back to the camera placeholder).
  const selectedPhotoUrls = useAnimalPhotoUrls(selected.filter((a) => a.id > 0).map((a) => a.id));
  const photoSrc = (a: SelectedAnimal) =>
    pendingPhotoUrls.get(a.id) ?? selectedPhotoUrls[String(a.id)] ?? a.fotoUrl;

  // Sub-wizard handlers (Jmix Search → Results → Details loop; see AnimalSubwizard). Each confirm
  // appends one animal; the sub-wizard remounts (key) so the next animal starts back at Zoek dier.
  function toSelected(a: SubwizardAnimal): SelectedAnimal {
    return {
      id: a.id,
      label: animalLabel(a),
      species: a.diersoort,
      naam: a.naam,
      chip: a.chipPasnummer,
      ras: a.ras,
      geslacht: a.geslacht,
      kleur: a.kleur,
      vacht: a.vacht,
      fotoUrl: a.fotoUrl,
    };
  }
  function addExistingAnimal(a: SubwizardAnimal, photo: File | null = null) {
    setSelected((cur) =>
      cur.some((x) => x.id === a.id) ? cur : [...cur, { ...toSelected(a), photo }],
    );
  }
  function addNewAnimal(n: NewAnimalResult, photo: File | null = null) {
    setSelected((cur) => [
      ...cur,
      {
        id: newKeySeq,
        label: `${n.naam} (${n.diersoortLabel ?? "?"})`,
        species: n.diersoortLabel,
        naam: n.naam,
        chip: n.chipPasnummer,
        ras: n.rasLabel,
        geslacht: n.geslacht,
        kleur: n.kleurLabel,
        vacht: n.vachtLabel,
        fotoUrl: null,
        photo,
        newAnimal: {
          naam: n.naam,
          diersoortId: n.diersoortId,
          geslacht: n.geslacht,
          geboortedatum: n.geboortedatum,
          chipPasnummer: n.chipPasnummer,
          rasId: n.rasId,
          kruising: n.kruising,
          vachtId: n.vachtId,
          kleurId: n.kleurId,
          gecastreerd: n.gecastreerd,
        },
      },
    ]);
    setNewKeySeq((k) => k - 1);
  }

  // Edit mode: pull the existing residency's editable fields so the form seeds 1:1 (a partial seed
  // would let the clear-then-apply save wipe the granular type fields the /stays payload omits).
  const { data: editResp } = useCustom({
    url: `intake/${editVerblijfId}`,
    method: "get",
    queryOptions: { enabled: editMode },
  });
  const editData = editResp?.data as IntakeEditView | undefined;
  const [seeded, setSeeded] = useState(false);

  // Seed the preselected animal once its label is known (new-intake launch from a detail tile).
  useEffect(() => {
    if (editMode || !locked || selected.length > 0 || animals.length === 0) return;
    const a = animals.find((x) => String(x.id) === preselectedAnimalId);
    if (a) setSelected([toSelected(a)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, locked, preselectedAnimalId, animals, selected.length]);

  // Step 3 — details (reset on type change = branch clearing)
  const [intakeDatum, setIntakeDatum] = useState<string>(todayIso());
  // The eigenaar step's "save my edits" hook — published by RelationPicker while its edit phase is
  // open and invoked by confirmStep, so Bevestigen persists the owner corrections.
  const relationSaveRef = useRef<(() => void) | null>(null);
  const registerRelationSave = useCallback((fn: (() => void) | null) => {
    relationSaveRef.current = fn;
  }, []);
  const [bringerId, setBringerId] = useState<number | null>(null);
  const [bringerLabel, setBringerLabel] = useState<string | null>(null);
  const [finderId, setFinderId] = useState<number | null>(null);
  // Set only by the "Geen vinder" button — see showTerms.
  const [finderNone, setFinderNone] = useState(false);
  // Which phase the relation picker is in, so the modal footer can step aside on its search card.
  const [relationPhase, setRelationPhase] = useState<"find" | "results" | "details" | "edit">("find");
  // Gezet zodra de picker een relatie heeft aangemaakt; de effect hieronder stapt door zodra die
  // relatie ook echt in de state staat.
  const [naAanmakenDoor, setNaAanmakenDoor] = useState(false);
  const [finderLabel, setFinderLabel] = useState<string | null>(null);
  const [gemeenteId, setGemeenteId] = useState<string>("");
  const [stray, setStray] = useState({ street: "", houseNumber: "", houseNumberAddition: "", postalCode: "", city: "", remark: "", dumped: false });
  // Stray ambulance step (Jmix StrayShelterIntakeAmbulanceFragment): a Ja/Nee toggle gating a
  // Dierenambulance picker + an optional ride number. `yes=false` persists null/null.
  const [ambulance, setAmbulance] = useState<{ yes: boolean; id: string; ride: string }>({ yes: false, id: "", ride: "" });
  // Stray vindersverklaring (Jmix StrayShelterIntakeTermsAgreementFragment): finder's declaration with
  // a required agreement + an optional signature. Session/audit data — kept in the WizardData JSON, not
  // written to asiel_verblijf (CreateIntakeRequest ignores these on bind).
  const [strayTerms, setStrayTerms] = useState({ accepted: false, signature: "", date: todayIso(), place: "" });
  const [surrender, setSurrender] = useState({ reason: "", remark: "" });
  // EXCHANGE Herkomst-asiel step (Jmix ExchangeSourceShelterFragment): client-side search over the
  // ASIEL-eligible shelters by name or city. The chosen shelter itself lives in bringer (the FK
  // resolveKlantInId reads for EXCHANGE — Jmix setBringer).
  const [shelterQuery, setShelterQuery] = useState("");
  // Owner-confirmation (Jmix ReturnAfterPlacementOwnerConfirmationFragment — RETURN asks "Is dit de
  // correcte eigenaar?", SURRENDER mode "Is dit de eigenaar die afstand doet?"): "Ja" pins that owner
  // as the intake relation, "Nee" reveals the manual picker. null = not yet answered.
  const [ownerConfirmChoice, setOwnerConfirmChoice] = useState<"yes" | "no" | null>(null);
  // Surrender economics (Phase 4): a manual cost-line table + the afstandsverklaring terms. Session
  // data in the WizardData JSON; the concept-factuur from these lines is a later slice (MR-4b).
  // btw = het percentage van de regel: uit de catalogus of de tariefmotor, anders zelf te kiezen.
  const [surrenderCosts, setSurrenderCosts] = useState<
    { id: number; description: string; amount: string; btw: number | null; korting?: string }[]
  >([]);
  const btwPercentages = useBtwPercentages();
  const [costSeq, setCostSeq] = useState(1);
  // "Product of tarief toevoegen" catalog picker for the cost lines (Jmix FactuurRegelDialog picker
  // mode — shared with the release adoption cost step via ProductTariefPicker).
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  // Tariff auto-proposal (Epic 16 / story 16-9 parity): the engine suggests a surrender fee from
  // species + process "Afstand"; the operator confirms or overrides. Read-only — the POST is a query.
  const { mutate: proposeTariff, isLoading: tariefBusy } = useCustomMutation<TariefVoorstelResponse>();
  const [tariefNotice, setTariefNotice] = useState<{ type: "ok" | "warn"; text: string } | null>(null);

  // Guards the once-per-wizard automatic tariff fill on entering the Afstandskosten step.
  const autoTariefDone = useRef(false);
  // The engine's answer, fetched as soon as the animal is known instead of when the step opens. The
  // call itself is fast (~25 ms), but starting it only on arrival meant the operator first saw an empty
  // kostenblok and then the line appearing — which reads as "het duurt lang". The release wizard already
  // primes its adoption proposal on the animals step; this is the same trick for Afstandskosten.
  const tariefVoorstelRef = useRef<TariefVoorstelResponse | null>(null);
  const tariefGevraagdVoor = useRef<string | null>(null);
  const [tariefTick, setTariefTick] = useState(0);

  // The declaration steps show the REAL document (the COMM_TEMPLATE afdruk the completion archives),
  // rendered server-side to page images — the same mechanism the release wizard already uses for the
  // plaatsingsovereenkomst. Empty when it can't render; the step then falls back to its own text.
  const [declPreviewPages, setDeclPreviewPages] = useState<string[]>([]);
  // Is the real afdruk still on its way? The plain-text clause list is the FALLBACK for when there is
  // no printable template; showing it while the print is loading made the step flash a different
  // document for two seconds before the real one replaced it.
  const [declPreviewBezig, setDeclPreviewBezig] = useState(false);
  // Print state for the server-rendered afdruk (the button replaces the old client-side print view).
  const [declPrintBezig, setDeclPrintBezig] = useState(false);
  const [declPrintError, setDeclPrintError] = useState<string | null>(null);

  function proposeSurrenderTariff(opts?: { auto?: boolean }) {
    const first = selected[0];
    const diersoort = first?.species ?? null;
    // Existing animal (real positive id) → let the server resolve age from the Dier; a freshly-entered
    // animal isn't persisted yet, so derive its age from the captured geboortedatum client-side.
    const animalId = first && first.id > 0 ? first.id : null;
    const leeftijdMaanden = monthsSince(first?.newAnimal?.geboortedatum ?? null);
    setTariefNotice(null);
    proposeTariff(
      {
        url: "tarief-voorstel",
        method: "post",
        values: { proces: "Afstand", diersoort, animalId, leeftijdMaanden },
      },
      {
        onSuccess: ({ data }) => applyTariefVoorstel(data, opts?.auto === true),
        onError: () => {
          if (!opts?.auto) setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.error") });
        },
      },
    );
  }

  // `auto` = the on-open fill. Jmix only ADDS a line on MATCHED there and stays quiet otherwise, so an
  // automatic run never greets the operator with a warning about a tariff they never asked for.
  function applyTariefVoorstel(r: TariefVoorstelResponse, auto = false) {
    if (auto && !(r.status === "MATCHED" && r.bedrag != null)) return;
    if (r.status === "MATCHED" && r.bedrag != null) {
      const omschrijving = r.omschrijving ?? r.regelNaam ?? "Tarief";
      setSurrenderCosts((cur) => [
        ...cur,
        { id: costSeq, description: omschrijving, amount: r.bedrag!.toFixed(2), btw: r.btwPercentage ?? null },
      ]);
      setCostSeq((n) => n + 1);
      setTariefNotice({
        type: "ok",
        text: t("inboeken.costs.propose.matched", { args: [omschrijving, formatEuro(r.bedrag, locale)] }),
      });
    } else if (r.status === "MATCHED") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.noAmount") });
    } else if (r.status === "MANUAL_QUOTE") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.manual") });
    } else if (r.status === "CONFLICT") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.conflict", { args: [r.conflictRegels.join(", ")] }) });
    } else {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.none") });
    }
  }

  const [surrenderTerms, setSurrenderTerms] = useState({
    accepted: false,
    signature: "",
    signedLater: false,
    date: todayIso(),
    place: "",
  });
  // Surrender payment (Jmix SurrenderShelterIntakePaymentFragment): per-method amounts (PIN/Contant
  // paid now, "Later" deferred) + a remark. Session data → WizardData JSON; the concept-factuur is
  // generated server-side on completion from the cost lines + paid amount.
  const [surrenderPayment, setSurrenderPayment] = useState({ pin: "", cash: "", later: "", reference: "" });
  // Which of the four payment shorthands is highlighted; the amount rows stay editable regardless.
  const [surrenderPayMethod, setSurrenderPayMethod] = useState<SurrenderPayMethod | null>(null);
  const [returnFields, setReturnFields] = useState({ reason: "", remark: "" });
  // Return economics (Jmix returnflow): restitution Ja/Nee + amount, then (only if amount > 0) the
  // payout step. Session data → WizardData JSON.
  const [returnRestitution, setReturnRestitution] = useState({ selected: false, amount: "" });
  // Payout methods are COMBINABLE (Jmix ReturnPaymentFragment: three method rows, each "Toevoegen"
  // auto-fills/clamps to the remaining amount; a summary table lists the added methods with delete).
  // One amount string per method — "" / ≤ 0 means the method is not part of the payout.
  const [returnPay, setReturnPay] = useState({ transfer: "", pin: "", cash: "", iban: "", remark: "" });
  const [returnPayMethod, setReturnPayMethod] = useState<ReturnPayTile | null>(null);
  const returnIbanSeeded = useRef(false);
  // NB: no birth-date field here — the backend (MR-A) deferred Dier.geboortedatum sync, so a date
  // entered here would be silently dropped. Add it back when the Dier-write slice lands.
  const [birth, setBirth] = useState({ motherName: "", remark: "" });
  const [motherQuery, setMotherQuery] = useState("");
  // Invisible carries: the wizard has no input for the stray geocode (lat/long) or the birth-mother
  // FK, but the edit's clear-then-apply would null them unless re-sent. Seed them from the prefill and
  // re-submit them as-is so an edit preserves what Jmix set (branch-cleared on a type change).
  const [carry, setCarry] = useState<{ lat: number | null; lng: number | null; motherId: number | null }>({
    lat: null,
    lng: null,
    motherId: null,
  });

  // PetBase chip-registration capture (Epic 3): one draft per chipped animal, keyed by the animal's id.
  const [chipDraft, setChipDraft] = useState<Record<number, ChipCaptureDraft>>({});

  const gemeenten = useFeed<GemeenteRow>("intake/reference/gemeenten", option === STRAY || option === BIRTH);
  // The chosen municipality's towns feed the Woonplaats dropdown. A city that came from the map's
  // reverse-geocode but isn't among them is appended, so switching gemeente never drops it silently.
  const woonplaatsOptions = useMemo(() => {
    const towns = gemeenten.find((g) => String(g.id) === gemeenteId)?.woonplaatsen ?? [];
    const list = towns.map((w) => ({ value: w, label: w }));
    const city = stray.city.trim();
    if (city && !towns.some((w) => w.toLowerCase() === city.toLowerCase())) {
      list.unshift({ value: city, label: city });
    }
    return list;
  }, [gemeenten, gemeenteId, stray.city]);
  const dierenambulances = useFeed<OptionRow>("intake/reference/dierenambulances", option === STRAY);
  // ASIEL-eligible source shelters for the EXCHANGE Herkomst-asiel step. Not fetched when the step is
  // dropped (receive-side launch: the source shelter is already known from the linked exchange).
  const exchangeShelters = useFeed<ExchangeShelterRow>(
    "intake/reference/exchange-shelters",
    option === EXCHANGE && !linkedExchangeId,
  );

  // Found-location geocoding (verblijf-wizard parity): forward (address → pin) + reverse (map click →
  // address fields), via the api/ proxy. The municipality the query/reverse uses is the selected
  // gemeente's label; a reverse-resolved municipality name is matched back to a gemeente option.
  const strayGemeenteLabel = gemeenten.find((g) => String(g.id) === gemeenteId)?.label ?? "";
  // Default map center = the current shelter's own geocoded address (Jmix parity), used until a pin exists.
  const strayMapCenter = useVestigingMapCenter(option === STRAY);
  // The declaration is signed at the branch, so "Plaats" starts there (Jmix prints "Plaats: ENSCHEDE").
  // Filled once, and only while still untouched, so a typed correction is never overwritten.
  const { data: vestigingResp } = useCustom<{ plaats?: string | null }>({
    url: "vestigingen/current",
    method: "get",
  });
  const vestigingPlaats = vestigingResp?.data?.plaats ?? null;
  // Readable from resetDetails(), which runs outside this effect's dependency graph.
  const vestigingPlaatsRef = useRef("");
  vestigingPlaatsRef.current = vestigingPlaats ?? "";
  useEffect(() => {
    if (!vestigingPlaats) return;
    setStrayTerms((s) => (s.place ? s : { ...s, place: vestigingPlaats }));
    setSurrenderTerms((s) => (s.place ? s : { ...s, place: vestigingPlaats }));
  }, [vestigingPlaats]);
  const { handlePick: handleStrayPick } = useFoundLocationGeocoding({
    enabled: option === STRAY,
    address: {
      street: stray.street,
      houseNumber: stray.houseNumber,
      houseNumberAddition: stray.houseNumberAddition,
      postalCode: stray.postalCode,
      city: stray.city,
      municipality: strayGemeenteLabel,
    },
    onResolvePin: (la, lo) => setCarry((c) => ({ ...c, lat: la, lng: lo })),
    onReversePopulate: (addr) => {
      // The pin was already placed by the click (onResolvePin); here we follow with the address fields.
      setStray((s) => ({
        ...s,
        city: addr.city ?? s.city,
        postalCode: addr.postalCode ?? s.postalCode,
        street: addr.street ?? s.street,
        houseNumber: addr.houseNumber ?? s.houseNumber,
        houseNumberAddition: addr.houseNumberAddition ?? s.houseNumberAddition,
      }));
      // Gemeente via the Jmix cascade (name → woonplaats → city), not just the reported municipality.
      const gemeente = resolveGemeenteId(gemeenten, addr);
      if (gemeente != null) setGemeenteId(String(gemeente));
    },
  });
  // Dier-ids already resident (open stay) — disabled in the picker so a double-intake is blocked up
  // front, not just as a 409 on submit (mirrors the Jmix hasOpenStayWithoutDepartureInfo guard).
  const residentIds = useFeed<number>("intake/reference/resident-animal-ids", !locked);
  const residentSet = useMemo(() => new Set(residentIds), [residentIds]);
  // The intake relation's full contact details (for the printed verklaring, the terms panel and the
  // summary's Vinder/Eigenaar/Relatie tile; the panel re-fetches the same url, which React Query
  // dedupes). STRAY → finder, SURRENDER/EXCHANGE → bringer, RETURN → owner (finder — the FK
  // resolveKlantInId reads for RETURN), generic → whichever slot the details step filled.
  const termsKlantId =
    option === SURRENDER || option === EXCHANGE ? bringerId : finderId;
  const { data: termsKlantResp } = useCustom({
    url: `intake/reference/klanten/${termsKlantId}`,
    method: "get",
    queryOptions: { enabled: termsKlantId != null },
  });
  const termsKlant = (termsKlantResp?.data as KlantDetail | undefined) ?? null;
  // BIRTH municipality pre-selects the current vestiging's gemeente (Jmix "Standaard de gemeente van de
  // huidige vestiging."); the operator can still override it.
  const { data: defGemResp } = useCustom({
    url: "intake/reference/default-gemeente",
    method: "get",
    queryOptions: { enabled: option === BIRTH },
  });
  const defaultGemeenteId = (defGemResp?.data as { gemeenteId?: number | null } | undefined)?.gemeenteId ?? null;
  useEffect(() => {
    // fires once per BIRTH entry / default load; clearing it later won't refire (deps unchanged)
    if (option === BIRTH && !gemeenteId && defaultGemeenteId != null) {
      setGemeenteId(String(defaultGemeenteId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option, defaultGemeenteId]);

  // Owner-confirmation answers: "Ja" pins the resolved owner as the intake relation (SURRENDER →
  // bringer, RETURN → finder — the same FK resolveKlantInId reads per flow); "Nee" clears it so the
  // operator picks manually in the next step (Jmix Yes → use owner / No → relation sub-wizard).
  function confirmOwnerYes() {
    setOwnerConfirmChoice("yes");
    if (knownOwner?.ownerId != null) {
      setRelation(knownOwner.ownerId, knownOwner.ownerLabel ?? null);
    }
  }
  function confirmOwnerNo() {
    setOwnerConfirmChoice("no");
    setRelation(null, null);
  }

  function resetDetails() {
    setBringerId(null);
    setBringerLabel(null);
    setOwnerConfirmChoice(null);
    setFinderId(null);
    setFinderLabel(null);
    setGemeenteId("");
    setStray({ street: "", houseNumber: "", houseNumberAddition: "", postalCode: "", city: "", remark: "", dumped: false });
    setAmbulance({ yes: false, id: "", ride: "" });
    // Keep the branch as "Plaats": the declaration is signed at the shelter, so a reset must not blank
    // it. The seeding effect only fires when the vestiging itself changes, so an emptied place would
    // never be refilled and the verklaring printed "Plaats: —".
    setStrayTerms({ accepted: false, signature: "", date: todayIso(), place: vestigingPlaatsRef.current });
    setSurrender({ reason: "", remark: "" });
    setShelterQuery("");
    setSurrenderCosts([]);
    setSurrenderTerms({
      accepted: false, signature: "", signedLater: false, date: todayIso(),
      place: vestigingPlaatsRef.current,
    });
    setSurrenderPayment({ pin: "", cash: "", later: "", reference: "" });
    setReturnFields({ reason: "", remark: "" });
    setReturnRestitution({ selected: false, amount: "" });
    setReturnPay({ transfer: "", pin: "", cash: "", iban: "", remark: "" });
    setReturnPayMethod(null);
    returnIbanSeeded.current = false;
    setBirth({ motherName: "", remark: "" });
    setMotherQuery("");
    setCarry({ lat: null, lng: null, motherId: null });
  }

  // Edit mode: seed every field from the prefill once (and only once the soorten feed is loaded so the
  // flow option is known). Uses the setters directly (not the onChange path) so it doesn't trip
  // resetDetails. `seeded` guards against re-running and clobbering the operator's subsequent edits.
  useEffect(() => {
    if (!editMode || seeded || !editData || soorten.length === 0) return;
    const opt = soorten.find((s) => s.id === editData.asielOpnameSoortId)?.option ?? "DEFAULT";
    setSoortId(editData.asielOpnameSoortId != null ? String(editData.asielOpnameSoortId) : "");
    setSelected([{
      id: editData.animalId,
      label: editData.animalLabel ?? `#${editData.animalId}`,
      species: null,
      naam: editData.animalLabel ?? `#${editData.animalId}`,
      chip: null, ras: null, geslacht: null, kleur: null, vacht: null, fotoUrl: null,
    }]);
    setIntakeDatum(editData.opnameDatum ?? todayIso());
    // klant_in seeds back into the slot each flow reads it from: SURRENDER's bringer, EXCHANGE's
    // source shelter (also bringer), else finder — so the clear-then-apply save can't wipe it.
    if (opt === SURRENDER || opt === EXCHANGE) {
      setBringerId(editData.klantInId);
      setBringerLabel(editData.klantInLabel);
    } else {
      setFinderId(editData.klantInId);
      setFinderLabel(editData.klantInLabel);
    }
    setGemeenteId(editData.gevondenGemeenteId != null ? String(editData.gevondenGemeenteId) : "");
    setStray({
      street: editData.strayFoundLocationStreet ?? "",
      houseNumber: editData.strayFoundLocationHouseNumber ?? "",
      houseNumberAddition: editData.strayFoundLocationHouseNumberAddition ?? "",
      postalCode: editData.strayFoundLocationPostalCode ?? "",
      city: editData.strayFoundLocationCity ?? "",
      remark: editData.strayFoundLocationRemark ?? "",
      dumped: editData.strayDumped === true,
    });
    setAmbulance({
      yes: editData.animalAmbulanceInId != null || (editData.animalAmbulanceInRideNumber ?? "") !== "",
      id: editData.animalAmbulanceInId != null ? String(editData.animalAmbulanceInId) : "",
      ride: editData.animalAmbulanceInRideNumber ?? "",
    });
    setSurrender({ reason: editData.surrenderReasonCode ?? "", remark: editData.surrenderReasonRemark ?? "" });
    setReturnFields({ reason: editData.returnReason ?? "", remark: editData.returnRemark ?? "" });
    setBirth({ motherName: editData.birthMotherAnimalName ?? "", remark: editData.birthRemark ?? "" });
    setCarry({
      lat: editData.strayFoundLocationLatitude,
      lng: editData.strayFoundLocationLongitude,
      motherId: editData.birthMotherAnimalId,
    });
    setVersion(editData.version);
    setSeeded(true);
  }, [editMode, seeded, editData, soorten]);

  // Receive-side preset (audit §3 row 2 — Jmix ShelterIntakePrepopulationService): an intake launched
  // from the Uitwisselingen inbox presets type = Uitwisseling and brenger = the exchange's source
  // shelter; the Herkomst-asiel step is dropped (keyed on linkedExchangeId, like Jmix keys the step
  // on linkedExchange). Re-seeds the brenger if a type-card re-click's resetDetails cleared it —
  // with the source step hidden there is no other way to restore it.
  useEffect(() => {
    if (!linkedExchangeId || soorten.length === 0) return;
    const exchangeSoort = soorten.find((s) => s.option === EXCHANGE);
    if (!exchangeSoort) return;
    if (!soortId) setSoortId(String(exchangeSoort.id));
    if (
      (!soortId || soortId === String(exchangeSoort.id)) &&
      bringerId == null &&
      linkedExchangeSource?.id != null
    ) {
      setBringerId(linkedExchangeSource.id);
      setBringerLabel(linkedExchangeSource.label ?? null);
    }
  }, [linkedExchangeId, linkedExchangeSource, soorten, soortId, bringerId]);

  // Submit mutation ONLY — `busy` gates the advance/finish buttons. The draft lifecycle below gets
  // its OWN mutation hook: sharing one hook let the mount-time draft POST hold `busy` true (under
  // StrictMode's double-invoked effect the observer could stick in `loading` forever, permanently
  // disabling Volgende), and a slow autosave PATCH must never lock navigation either.
  const { mutate, isLoading: busy } = useCustomMutation();
  const { mutate: draftMutate } = useCustomMutation();

  // ── generic WizardData draft lifecycle (ADR adr-wizard-data-generic-store) ───────────────────────
  // Open a DRAFT when the wizard mounts; PATCH it per step (best-effort); complete() runs the domain
  // commit server-side. A ref guards against React StrictMode's double-invoke creating two drafts.
  const [draftId, setDraftId] = useState<string | null>(null);
  const draftRequested = useRef(false);
  useEffect(() => {
    if (draftRequested.current) return;
    draftRequested.current = true;
    draftMutate(
      { url: "wizard", method: "post", values: { wizardType: "INTAKE" } },
      { onSuccess: (res) => setDraftId(((res as { data?: { id?: string } })?.data?.id) ?? null) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function persistDraft() {
    if (!draftId) return;
    // fire-and-forget: a failed autosave must never block the user mid-wizard
    draftMutate({ url: `wizard/${draftId}`, method: "patch", values: { data: buildValues() } }, {});
  }

  // Sub-wizard dedup/guard sets (Jmix "al geselecteerd" / "al aanwezig" card blocks).
  const selectedIdSet = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const relationLabel =
    option === STRAY ? "inboeken.field.vinder"
    : option === SURRENDER || option === RETURN ? "inboeken.field.eigenaar"
    : option === EXCHANGE ? "inboeken.exchangeSource.title"
    : "inboeken.field.relatie";
  // EXCHANGE routes the relation to bringer like SURRENDER: the source shelter is the brenger
  // (Jmix ExchangeSourceShelterFragment.setBringer; klant_in via the resolveKlantInId fallback).
  const relationViaBringer = option === SURRENDER || option === EXCHANGE;
  const relationId = relationViaBringer ? bringerId : finderId;
  const relationName = relationViaBringer ? bringerLabel : finderLabel;
  const setRelation = (id: number | null, label: string | null) => {
    if (relationViaBringer) {
      setBringerId(id);
      setBringerLabel(label);
    } else {
      setFinderId(id);
      setFinderLabel(label);
    }
  };

  function buildValues(): Record<string, unknown> {
    const values: Record<string, unknown> = {
      asielOpnameSoortId: soortId ? Number(soortId) : null,
      intakeDateTime: intakeDatum ? `${intakeDatum}T00:00:00` : null,
      animals: selected.map((a) => (a.newAnimal ? { newAnimal: a.newAnimal } : { animalId: a.id })),
      bringerId,
      finderId,
    };
    if (editMode) {
      values.targetVerblijfId = Number(editVerblijfId);
      values.version = version;
    }
    // Receiving-side intake of an exchanged animal — completing this intake completes the exchange.
    if (linkedExchangeId) {
      values.linkedExchangeId = Number(linkedExchangeId);
    }
    if (option === STRAY) {
      values.animalAmbulanceInId = ambulance.yes && ambulance.id ? Number(ambulance.id) : null;
      values.animalAmbulanceInRideNumber = ambulance.yes ? ambulance.ride || null : null;
      values.gevondenGemeenteId = gemeenteId ? Number(gemeenteId) : null;
      values.strayFoundLocationStreet = stray.street || null;
      values.strayFoundLocationHouseNumber = stray.houseNumber || null;
      values.strayFoundLocationHouseNumberAddition = stray.houseNumberAddition || null;
      values.strayFoundLocationPostalCode = stray.postalCode || null;
      values.strayFoundLocationCity = stray.city || null;
      values.strayFoundLocationRemark = stray.remark || null;
      values.strayDumped = stray.dumped;
      // re-send the invisible geocode so an edit preserves (rather than wipes) it
      values.strayFoundLocationLatitude = carry.lat;
      values.strayFoundLocationLongitude = carry.lng;
      // vindersverklaring — session/audit data (WizardData JSON only; not in asiel_verblijf)
      if (showTerms) {
        values.strayTermsAgreementAccepted = strayTerms.accepted;
        values.strayTermsSignature = strayTerms.signature || null;
        values.strayTermsDate = strayTerms.date || null;
        values.strayTermsPlace = strayTerms.place || null;
      }
    } else if (option === SURRENDER) {
      values.surrenderReasonCode = surrender.reason || null;
      values.surrenderReasonRemark = surrender.remark || null;
      // Owner-confirmation answer — audit data (WizardData JSON only; the bringer FK carries the effect).
      if (ownerConfirmChoice != null) {
        values.surrenderOwnerConfirmed = ownerConfirmChoice === "yes";
      }
      // economics — session/audit data (WizardData JSON only; concept-factuur is a later slice)
      if (showSurrenderEcon) {
        values.surrenderCostLines = surrenderCosts.map((c) => ({
          description: c.description || null,
          amount: parseAmount(c.amount),
          btwPercentage: c.btw,
          korting: kortingVanRegel(c) || null,
        }));
        values.surrenderCostTotal = surrenderCostTotal;
        values.surrenderTermsAgreementAccepted = surrenderTerms.accepted;
        values.surrenderTermsSignature = surrenderTerms.signature || null;
        values.surrenderTermsSignedLater = surrenderTerms.signedLater;
        values.surrenderTermsDate = surrenderTerms.date || null;
        values.surrenderTermsPlace = surrenderTerms.place || null;
        // payment — drives the server-side concept-factuur (paid = PIN + Contant; "later" is deferred)
        values.surrenderPaymentPin = parseAmount(surrenderPayment.pin);
        values.surrenderPaymentCash = parseAmount(surrenderPayment.cash);
        values.surrenderPaymentLater = parseAmount(surrenderPayment.later);
        values.surrenderPaymentReference = surrenderPayment.reference || null;
      }
    } else if (option === RETURN) {
      values.returnReason = returnFields.reason || null;
      values.returnRemark = returnFields.remark || null;
      // Owner-confirmation answer (Jmix ShelterIntake.returnOwnerConfirmed) — audit data.
      if (ownerConfirmChoice != null) {
        values.returnOwnerConfirmed = ownerConfirmChoice === "yes";
      }
      if (showReturnEcon) {
        values.returnRestitutionSelected = returnRestitution.selected;
        values.returnRestitutionAmount = returnRestitution.selected ? parseAmount(returnRestitution.amount) : 0;
        // Voorwaarden + handtekening — Jmix STEP_RETURN_TERMS reuses the surrender terms fragment,
        // which writes the same surrenderTerms* fields; mirror that key reuse in the JSON.
        values.surrenderTermsAgreementAccepted = surrenderTerms.accepted;
        values.surrenderTermsSignature = surrenderTerms.signature || null;
        values.surrenderTermsSignedLater = surrenderTerms.signedLater;
        values.surrenderTermsDate = surrenderTerms.date || null;
        values.surrenderTermsPlace = surrenderTerms.place || null;
        if (showReturnPayment) {
          // Jmix applySelectionTo: a comma-joined summary of the added methods + the paid-out total.
          values.returnPaymentMethod = returnPayMethodSummary || null;
          values.returnPaymentAmount = returnPaidOut;
          values.returnPaymentIban = returnPayTransfer > 0 ? returnPay.iban.trim() || null : null;
          values.returnPaymentReference = returnPay.remark || null;
          values.returnPaymentBreakdown = {
            transfer: returnPayTransfer,
            pin: returnPayPin,
            cash: returnPayCash,
          };
        }
      }
    } else if (option === BIRTH) {
      // operator-picked gemeente (defaults to the vestiging's); resolveGemeenteId honours a pick for
      // every type, so BIRTH overrides the vestiging-town auto-resolution.
      values.gevondenGemeenteId = gemeenteId ? Number(gemeenteId) : null;
      values.birthMotherAnimalName = birth.motherName || null;
      values.birthRemark = birth.remark || null;
      values.birthMotherAnimalId = carry.motherId;
    }
    // PetBase chip captures — only when the step ran and PetBase is reachable (an outage → no captures →
    // no rows enqueued, mirroring the Jmix skip). The server reads these from the WizardData JSON and
    // enqueues a REGISTER job per chipped animal on /complete (CreateIntakeRequest ignores them).
    if (showChipStep && !chipOutage) {
      values.chipCaptures = chippedAnimals.map(({ sel }) => {
        const d = chipDraft[sel.id];
        return {
          animalIndex: selected.findIndex((s) => s.id === sel.id),
          chipNumber: d?.chipNumber?.trim() || null,
          aanvoerType: d?.aanvoerType || null,
          countryOfOrigin: d?.countryOfOrigin?.trim() || null,
          motherChipNumber: d?.aanvoerType === AANVOER_GEBOORTE ? d?.motherChipNumber?.trim() || null : null,
          newChipInsertOn: d?.aanvoerType === AANVOER_NIEUWE_CHIP ? d?.newChipInsertOn || null : null,
          chipReadDate: d?.chipReadDate || null,
          eventDate: d?.eventDate || null,
          chipperCibgNumber: d?.chipperCibgNumber || null,
        };
      });
    }
    return values;
  }

  // The /complete response's `animalIds` are parallel to the request's animals array — i.e. the
  // `selected` order (slice 3b). Shared by the photo upload and the completion navigation.
  function completionAnimalIds(res: unknown): number[] {
    const animalIds = (res as { data?: { animalIds?: unknown } } | undefined)?.data?.animalIds;
    if (!Array.isArray(animalIds)) return [];
    return animalIds.filter((id): id is number => typeof id === "number");
  }

  // The sub-wizard's captured photos become the primary Dier photos (Jmix: the pending photo is
  // persisted on completion). Best-effort and non-blocking: a failed upload never fails the intake
  // that already committed.
  function uploadPendingPhotos(res: unknown) {
    const animalIds = completionAnimalIds(res);
    if (animalIds.length === 0) return;
    selected.forEach((s, i) => {
      const id = animalIds[i];
      if (s.photo && typeof id === "number") {
        void uploadIntakeAnimalPhoto(id, s.photo).catch(() => undefined);
      }
    });
  }

  function submit() {
    setNotice(null);
    const values = buildValues();
    if (draftId) {
      // PATCH the final state into the draft, then run the server-side domain commit + mark COMPLETED.
      mutate(
        { url: `wizard/${draftId}`, method: "patch", values: { data: values } },
        {
          onSuccess: () =>
            mutate(
              { url: `wizard/${draftId}/complete`, method: "post", values: {} },
              {
                onSuccess: (res) => {
                  uploadPendingPhotos(res);
                  onDone(completionAnimalIds(res));
                },
                onError: (err) => setNotice(noticeFor(err)),
              },
            ),
          onError: (err) => setNotice(noticeFor(err)),
        },
      );
    } else {
      // No draft yet (its opening POST lost a race with a fast finish). Completing through POST
      // /intake would work, but that endpoint takes only the domain fields — the signature and the
      // terms acceptance live solely in the draft JSON, so the signed declaration would never be
      // archived and nobody would notice. Open a draft now and finish through it instead.
      mutate(
        { url: "wizard", method: "post", values: { wizardType: "INTAKE" } },
        {
          onSuccess: (res) => {
            const id = (res as { data?: { id?: string } })?.data?.id ?? null;
            if (!id) {
              setNotice("error");
              return;
            }
            setDraftId(id);
            mutate(
              { url: `wizard/${id}`, method: "patch", values: { data: values } },
              {
                onSuccess: () =>
                  mutate(
                    { url: `wizard/${id}/complete`, method: "post", values: {} },
                    {
                      onSuccess: (done) => {
                        uploadPendingPhotos(done);
                        onDone(completionAnimalIds(done));
                      },
                      onError: (err) => setNotice(noticeFor(err)),
                    },
                  ),
                onError: (err) => setNotice(noticeFor(err)),
              },
            );
          },
          onError: (err) => setNotice(noticeFor(err)),
        },
      );
    }
  }


  // "Wilde kat" is cats-only (Jmix isCatSpecies). wizard-jmix-parity: animals are now picked BEFORE
  // the type, so the Wilde kat card greys out when a non-cat is selected (exactly what the Jmix grid
  // does). wildCatBlocked stays as the belt-and-braces gate on the Dieren step for the edit-back path
  // (type already wildcat, operator swaps in a non-cat). Declared BEFORE the steps array —
  // renderAnimals() reads wildCatBlocked at step-build time (would hit a const TDZ).
  const selectedSoort = soorten.find((s) => String(s.id) === soortId);
  const isWildCatType = selectedSoort ? typeKey(selectedSoort) === "wildcat" : false;
  // Jmix ShelterIntakeTypeSelectionFragment skips animals whose species is unknown/blank in the
  // cats-only check — only a KNOWN non-cat species greys out the Wilde-kat card.
  const wildCatIncompatible = selected.some((s) => (s.species ?? "").trim() !== "" && !isCat(s.species));
  const wildCatBlocked = isWildCatType && wildCatIncompatible;
  // Animal-count bound (Jmix SelectAnimalCountFragment: 1 single, else 2–10): the Dieren step gates
  // on matching the chosen count exactly (a locked launch is always a single fixed animal).
  const plannedCount = locked ? 1 : multi ? Number(countInput) || 0 : 1;
  const countInvalid = multi == null || (multi && (plannedCount < 2 || plannedCount > MAX_ANIMAL_COUNT));
  const animalCountBlocked = selected.length > MAX_ANIMAL_COUNT;

  // The vindersverklaring is a STRAY-only step shown once a finder is selected (Jmix skips it when
  // "Geen vinder"); edit mode has no WizardData to seed it, so it's a new-intake concern only.
  // The vindersverklaring is skipped ONLY when the operator pressed "Geen vinder". Searching on an
  // address or e-mail and not landing on an existing relation still means there IS a finder, so the
  // declaration must be drawn up for them — an empty finderId is not the same as "no finder".
  const showTerms = !editMode && option === STRAY && !finderNone;
  // Owner-confirmation step — new-intake SURRENDER and RETURN once a real (existing, history-bearing)
  // animal is picked; a brand-new animal (synthetic id ≤ 0) has no owner on file. The step shows
  // unconditionally for those (Jmix has it as a fixed step), with the known owner resolved server-side
  // (the known-owner resolution chain is shared between both flows).
  const firstSelectedId = selected[0]?.id ?? null;
  const ownerConfirmStepEnabled =
    !editMode && (option === SURRENDER || option === RETURN) && firstSelectedId != null && firstSelectedId > 0;
  const knownOwnerQuery = useCustom({
    url: `intake/reference/animals/${firstSelectedId}/known-owner`,
    method: "get",
    queryOptions: { enabled: ownerConfirmStepEnabled },
  });
  // A 404 (no owner on file) yields no body; guard against a non-object so an empty payload reads as null.
  const knownOwnerData = knownOwnerQuery.data?.data;
  const knownOwner =
    ownerConfirmStepEnabled && knownOwnerData && !Array.isArray(knownOwnerData)
      ? (knownOwnerData as KnownOwnerView)
      : null;
  // Must answer Ja/Nee before leaving the step when an owner was found; no owner = nothing to confirm.
  const ownerConfirmBlocked = ownerConfirmStepEnabled && knownOwner != null && ownerConfirmChoice === null;
  // Nothing on file? Then there is nothing to confirm: the step would be a dead screen saying "kies de
  // brenger in de volgende stap" that you have to click past, so it is dropped and the wizard lands on
  // the relation search straight away.
  //
  // Gegrond op isLoading, NIET op "data is nog undefined": geen bekende eigenaar is een 404, en daarbij
  // blijft `data` voorgoed undefined. Dat liet de dode stap juist altijd staan — precies het scherm dat
  // hier weg hoort. isLoading valt ook op een fout terug naar false, dus de stap verdwijnt dan ook.
  const showOwnerConfirm = ownerConfirmStepEnabled && (knownOwnerQuery.isLoading || knownOwner != null);

  // The relation-search step runs for the owner flows whenever the known owner was NOT confirmed —
  // i.e. answered "Nee", or there was no known owner to confirm at all. Deliberately independent of
  // whether a relation is already picked, so the step list stays stable while searching.
  // STRAY joins them with the finder: Jmix gives the finder the same full search screen (e-mail /
  // adres / nieuwe relatie) — the small inline picker on the Herkomst card was a shrunken stand-in,
  // and because nothing there could set a finder, the vindersverklaring step never appeared either.
  const relationFindStepEnabled =
    !locked && ((option === SURRENDER || option === RETURN) && ownerConfirmChoice !== "yes"
      || option === STRAY);
  // An afstand/retour intake needs an owner — don't leave the step without one (Jmix parity). A stray
  // may genuinely have no finder ("Geen vinder"), so that flow is never blocked.
  const relationFindBlocked =
    relationFindStepEnabled && option !== STRAY && relationId == null;

  // Surrender economics steps (Afstandskosten + Voorwaarden + Betaling) — new-intake SURRENDER only.
  const showSurrenderEcon = !editMode && option === SURRENDER;
  const surrenderRegelTotal = surrenderCosts.reduce((sum, c) => sum + (parseAmount(c.amount) ?? 0), 0);
  // Korting staat per regel; zie KortingVeld voor het waarom.
  const surrenderKortingBedrag = kortingTotaal(surrenderCosts);
  const surrenderKortingTeHoog = surrenderCosts.some(kortingRegelTeHoog);
  const surrenderCostTotal = afgerondeEuro(surrenderRegelTotal - surrenderKortingBedrag);
  const surrenderBtwOntbreekt = kostenregelZonderBtw(surrenderCosts, parseAmount);
  const surrenderPaidNow = (parseAmount(surrenderPayment.pin) ?? 0) + (parseAmount(surrenderPayment.cash) ?? 0);
  const surrenderPaymentPlanned = surrenderPaidNow + (parseAmount(surrenderPayment.later) ?? 0);
  // Meer registreren dan er te betalen staat zou betekenen dat het asiel geld teruggeeft. Dat gebeurt
  // alleen bij retour na plaatsing, en daar is de restitutiestap voor -- niet deze.
  const surrenderOordeel = betalingOordeel(surrenderCostTotal, surrenderPaymentPlanned);
  const surrenderTeveel = surrenderOordeel.fout !== null;
  // Owner must agree; a signature is required too unless "tekent later" is chosen (Jmix parity).
  const surrenderTermsBlocked =
    !surrenderTerms.accepted || (!surrenderTerms.signedLater && !surrenderTerms.signature);

  // Return economics steps (Restitutie → Voorwaarden → Betaling) — new-intake RETURN only; the
  // payment step only shows when a restitution amount > 0 (Jmix isReturnPaymentRequired).
  const showReturnEcon = !editMode && option === RETURN;
  const returnRefund = returnRestitution.selected ? (parseAmount(returnRestitution.amount) ?? 0) : 0;
  const showReturnPayment = showReturnEcon && returnRefund > 0;

  // Previous-adoption overview for the restitution step ("Overzicht retour"): placement date, the
  // adoption invoice's lines + totals, the "Maximaal" cap and the invoice owner (mismatch warning).
  const { data: restiResp } = useCustom({
    url: `intake/reference/animals/${firstSelectedId}/return-restitution`,
    method: "get",
    queryOptions: { enabled: showReturnEcon && firstSelectedId != null && firstSelectedId > 0 },
  });
  const restiData = restiResp?.data;
  const resti =
    showReturnEcon && restiData && !Array.isArray(restiData) ? (restiData as ReturnRestitutionView) : null;
  // Jmix caps the refund at the paid total (else the €200 fallback the server also applies).
  const returnMaxRefund = resti?.maxRefund ?? 200;

  // Multi-method payout math (Jmix ReturnPaymentFragment): per-method committed amounts, the
  // paid-out total, and the comma-joined method summary that lands in the JSON.
  const returnPayTransfer = parseAmount(returnPay.transfer) ?? 0;
  const returnPayPin = parseAmount(returnPay.pin) ?? 0;
  const returnPayCash = parseAmount(returnPay.cash) ?? 0;
  const returnPaidOut = returnPayTransfer + returnPayPin + returnPayCash;
  const returnPayMethodSummary = [
    returnPayTransfer > 0 ? t("inboeken.returnPayment.transfer") : null,
    returnPayPin > 0 ? t("inboeken.returnPayment.pin") : null,
    returnPayCash > 0 ? t("inboeken.returnPayment.cash") : null,
  ]
    .filter(Boolean)
    .join(", ");
  // A bank transfer requires an IBAN that also passes the mod-97 structure check (Jmix
  // ReturnPaymentFragment: required + "Voer een geldig IBAN-nummer in.").
  const returnPaymentBlocked = showReturnPayment && returnPayTransfer > 0 && !isValidIban(returnPay.iban);
  // Uitbetalen is betalen in de andere richting: niet meer uitbetalen dan er terug moet, en wat er
  // van de restitutie blijft staan wordt bevestigd. Zelfde regel, zelfde vraag.
  const returnPayOordeel = betalingOordeel(returnRefund, returnPaidOut);

  // Prefill the payout IBAN from the owner's bank account once (Jmix reads the resolved owner's
  // bankRekeningNummer); the operator can still overtype it.
  useEffect(() => {
    if (!showReturnEcon || returnIbanSeeded.current) return;
    const iban = resti?.ownerIban?.trim();
    if (iban) {
      returnIbanSeeded.current = true;
      setReturnPay((p) => (p.iban ? p : { ...p, iban }));
    }
  }, [showReturnEcon, resti]);

  // ── PetBase chip step (Epic 3) ──────────────────────────────────────────────────────────────────
  // Selected animals that carry a chip — chipless ones never reach PetBase (Jmix
  // intakeHasAnyChipNumber: any animal whose chipPasnummer is non-blank). A brand-new animal counts
  // too: Jmix creates the Dier during the animal step, so a chip typed in the sub-wizard is already on
  // the entity by the time the step sequence is resolved. Reading only the animals feed skipped exactly
  // those — booking in a new, chipped animal then silently never registered it.
  const chippedAnimals = useMemo(
    () =>
      selected
        .map((a) => {
          const summary = animals.find((x) => x.id === a.id);
          const chip = (a.newAnimal ? a.newAnimal.chipPasnummer : summary?.chipPasnummer ?? a.chip) ?? "";
          return { sel: a, summary, chip: chip.trim() };
        })
        .filter(({ chip }) => chip !== ""),
    [selected, animals],
  );
  // Chipper reference feed — fetched once there is a chipped animal to register (new intake only).
  // Flow gate (ShelterIntakeWizard.appendStepFragments :647-650, :671-674): Jmix only appends the
  // chip step to the SURRENDER and RETURN sequences — a stray/birth intake never registers an
  // AANVOER, so don't show the step (or enqueue jobs) for those flows.
  const chipFlowEligible = option === SURRENDER || option === RETURN;
  const chipFeedEnabled = !editMode && chipFlowEligible && chippedAnimals.length > 0;
  const { data: chipIdResp } = useCustom({
    url: "intake/petbase/identifiers",
    method: "get",
    queryOptions: { enabled: chipFeedEnabled },
  });
  const chipIdentifiers = (chipIdResp?.data as ChipIdentifiersResponse | undefined) ?? null;
  const chipConfigured = chipIdentifiers?.configured ?? false;
  const chipReachable = chipIdentifiers?.reachable ?? true;
  // PetBase configured but unreachable: hide the form, show the outage notice, let the operator continue.
  const chipOutage = chipConfigured && !chipReachable;
  const showChipStep = chipFeedEnabled && chipConfigured;
  const chipStepValid =
    chipOutage || chippedAnimals.every(({ sel }) => chipCaptureErrors(chipDraft[sel.id]).length === 0);

  // Seed a default draft per chipped animal once (chip number from the feed; NL + today defaults).
  useEffect(() => {
    if (!showChipStep || chipOutage) return;
    setChipDraft((cur) => {
      let changed = false;
      const next = { ...cur };
      for (const { sel, chip } of chippedAnimals) {
        if (!next[sel.id]) {
          next[sel.id] = {
            aanvoerType: "AANVOER_NIEUWE_EIGENAAR",
            eventDate: todayIso(),
            chipNumber: chip,
            countryOfOrigin: "NL",
            motherChipNumber: "",
            newChipInsertOn: "",
            chipReadDate: todayIso(),
            chipperCibgNumber: "",
          };
          changed = true;
        }
      }
      return changed ? next : cur;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChipStep, chipOutage, chippedAnimals]);

  function setChip(id: number, patch: Partial<ChipCaptureDraft>) {
    setChipDraft((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }));
  }

  // Collapsed-bar summaries (Jmix StepCard.createCollapsedSummary): "Label: value1 | value2".
  const sum = (label: string, ...values: (string | null | undefined)[]) =>
    `${label}: ${values.map((v) => (v == null || v === "" ? "-" : v)).join(" | ")}`;

  // The animals bar mirrors the Jmix add-animals summary panel: one row per animal plus a
  // "Toevoegen" chip that bumps the planned count and reopens the Dieren step.
  // Jmix add-animals summary rows: 56px photo thumbnail + "chip | naam | soort | ras | geslacht |
  // kleur | vacht" per animal (ShelterIntakeAddAnimalsFragment), plus the Toevoegen chip.
  const animalsSummary = (
    <div className="fawkes-stepwiz__animal-rows">
      {selected.length === 0 && <span>{sum(t("inboeken.step.animals"))}</span>}
      {selected.map((a) => (
        <div className="fawkes-stepwiz__animal-row" key={a.id}>
          <AnimalThumb fotoUrl={photoSrc(a)} alt={a.label} noPhotoLabel={t("inboeken.results.noPhoto")} />
          <span className="fawkes-stepwiz__animal-row-label">
            {animalRowText({
              chip: a.chip,
              naam: a.naam,
              soort: a.species,
              ras: a.ras,
              geslacht: a.geslacht,
              kleur: a.kleur,
              vacht: a.vacht,
            })}
          </span>
        </div>
      ))}
    </div>
  );
  // Key-based step model (dynamic): type-specific steps insert between Gegevens and Controle, so step
  // gates key off the step's `key`, not a fixed index. wizard-jmix-parity: the Jmix step ORDER — the
  // animal count + the animals come BEFORE the intake type (ShelterIntakeWizardDefinitions), exactly
  // what operators know from the Jmix dialog.
  // The animal sub-wizard drives its own Zoeken/Terug/Bevestig-dier buttons while more animals are
  // needed — the card-level nav hides meanwhile (Jmix hides the parent nav for add-animals).
  const animalSubwizardActive = !locked && selected.length < Math.max(plannedCount, 1);
  // Same step, two positions (see the ordering note below), so it is built once.
  const relationFindStepDef = {
    key: "relationFind",
    title: t(
      option === STRAY
        ? "inboeken.relation.findFinderTitle"
        : option === RETURN
          ? "inboeken.returnOwnerConfirm.title"
          : "inboeken.step.ownerConfirm",
    ),
    // No recap band for the finder either — the recap area shows the selected animals only, like the
    // other steps. A "Vinder: …" bar with its own pencil above the card was one affordance too many.
    summary: undefined,
    content: renderRelationFind(),
    hasBodyTitle: true,
    // While the picker shows its search card, that card carries the navigation — two Terug buttons
    // stacked would be nonsense. The later phases hand it back to the modal footer.
    hideActions: relationPhase === "find",
  };
  const stepDefs = [
    // No count recap card — the user wants only the selected animals shown as recap, nothing else.
    { key: "count", title: t("inboeken.count.header"), summary: undefined, content: renderCount() },
    // No redundant "Dieren" intro heading — the Zoek-dier card + recap already frame this step.
    { key: "animals", title: t("inboeken.step.animals"), summary: animalsSummary, content: renderAnimals(), hideActions: animalSubwizardActive, hasBodyTitle: true },
    // No type recap card either — keep the recap area to the selected animals only.
    { key: "type", title: t("inboeken.type.header"), summary: undefined, content: renderType() },
    ...(showOwnerConfirm
      ? [{
          key: "ownerConfirm",
          title: t(option === RETURN ? "inboeken.returnOwnerConfirm.title" : "inboeken.step.ownerConfirm"),
          // No recap card — the recap area shows the selected animals only.
          summary: undefined,
          content: renderOwnerConfirm(),
          hasBodyTitle: true,
        }]
      : []),
    // The Jmix relation sub-wizard as its own step: once the known owner is rejected (or there is
    // none), the operator searches an existing relation by e-mail/adres and can create a new one —
    // instead of the small inline picker that used to sit on the Gegevens card.
    // Owner flows search the relation BEFORE the details card; the stray flow searches its finder
    // AFTER the found location — the Jmix order is
    // intakeTypeSelection → strayAmbulance → strayFoundLocation → relationSubwizard → strayTerms.
    ...(relationFindStepEnabled && option !== STRAY ? [relationFindStepDef] : []),
    ...(option === STRAY ? [{
      key: "strayAmbulance",
      title: t("inboeken.ambulance.question"),
      // No recap card for this step — the answer isn't worth a band across the body.
      summary: undefined,
      content: renderStrayAmbulance(),
      hasBodyTitle: true,
    }] : []),
    // EXCHANGE swaps the generic Gegevens card for the Jmix Herkomst-asiel step (the source shelter
    // IS the intake relation); on a receive-side launch (linkedExchangeId) the step is dropped
    // entirely — type + brenger are preset (Jmix: "bron-stap vervalt").
    ...(option === EXCHANGE
      ? linkedExchangeId
        ? []
        : [{
            key: "exchangeSource",
            title: t("inboeken.exchangeSource.title"),
            summary: sum(t("inboeken.exchangeSource.title"), bringerLabel),
            content: renderExchangeSource(),
            hasBodyTitle: true,
          }]
      : showReturnEcon
      // A retour-na-plaatsing has no Gegevens step in Jmix: its sequence is intakeTypeSelection →
      // returnOwnerConfirmation → returnRestitution → returnTerms → returnPayment →
      // returnChipRegistration → finalSummary (ShelterIntakeWizardDefinitions). The reason and the
      // remark live on the Restitutie step, the owner on the confirmation step, and the intake date
      // is simply today — so the card was left showing nothing but "Datum binnenkomst". Edit mode
      // has no Restitutie step and does still need that card, so it keeps it.
      ? []
      : [{
          key: "details",
          // STRAY calls this step "Vindplaats" ("Waar is het dier aangetroffen?"), not "Gegevens" —
          // verified against the running Jmix on acc.
          title: t(option === STRAY ? "inboeken.foundLocation.title" : "inboeken.step.details"),
          // Nothing worth recapping for a stray: this step no longer carries the date or the relation.
          summary: option === STRAY
            ? undefined
            : sum(t("inboeken.step.details"), intakeDatum ? formatDate(intakeDatum, locale) : null, relationName),
          content: renderDetails(),
          hasBodyTitle: option === STRAY,
        }]),
    ...(relationFindStepEnabled && option === STRAY ? [relationFindStepDef] : []),
    ...(showTerms
      ? [{
          key: "terms",
          title: t("inboeken.step.terms"),
          summary: sum(t("inboeken.step.terms"), strayTerms.accepted ? t("wizard.agreed") : null),
          content: renderTerms(),
          hasBodyTitle: true,
        }]
      : []),
    ...(showSurrenderEcon
      ? [
          { key: "surrenderCosts", title: t("inboeken.step.costs"), summary: sum(t("inboeken.step.costs"), formatEuro(surrenderCostTotal, locale)), content: renderSurrenderCosts(), hasBodyTitle: true },
          { key: "surrenderTerms", title: t("inboeken.step.surrenderTerms"), summary: sum(t("inboeken.step.surrenderTerms"), surrenderTerms.accepted ? t("wizard.agreed") : null), content: renderTermsAgreement(bringerId), hasBodyTitle: true },
          { key: "surrenderPayment", title: t("inboeken.step.payment"), summary: sum(t("inboeken.step.payment"), formatEuro(surrenderPaidNow, locale)), content: renderSurrenderPayment(), hasBodyTitle: true },
        ]
      : []),
    ...(showReturnEcon
      ? [
          {
            key: "returnRestitution",
            title: t("inboeken.step.restitution"),
            summary: sum(t("inboeken.step.restitution"), returnRestitution.selected ? formatEuro(returnRefund, locale) : t("common.no")),
            content: renderReturnRestitution(),
            hasBodyTitle: true,
          },
          // Jmix RETURN sequence: Restitutie → Voorwaarden (STEP_RETURN_TERMS reuses the
          // afstands-voorwaardenfragment) → Betaling (alleen bij bedrag > 0).
          {
            key: "returnTerms",
            title: t("inboeken.step.surrenderTerms"),
            summary: sum(t("inboeken.step.surrenderTerms"), surrenderTerms.accepted ? t("wizard.agreed") : null),
            content: renderTermsAgreement(finderId),
            hasBodyTitle: true,
          },
        ]
      : []),
    ...(showReturnPayment
      ? [{
          key: "returnPayment",
          title: t("inboeken.step.payment"),
          summary: sum(
            t("inboeken.step.payment"),
            returnPayMethodSummary || "-",
            formatEuro(returnPaidOut, locale),
          ),
          content: renderReturnPayment(),
          hasBodyTitle: true,
        }]
      : []),
    ...(showChipStep
      ? [{ key: "chip", title: t("inboeken.step.chip"), summary: sum(t("inboeken.step.chip"), String(chippedAnimals.length)), content: renderChipStep(), hasBodyTitle: true }]
      : []),
    // hasBodyTitle: the per-flow summaries carry their own Jmix header ("Samenvatting registratie" +
    // info line) — the card-level h3 would double it (audit §1 row 5).
    { key: "summary", title: t("inboeken.step.summary"), summary: t("inboeken.step.summary"), content: renderSummary(), hasBodyTitle: true },
  ];
  const currentKey = stepDefs[Math.min(step, stepDefs.length - 1)]?.key;

  // Once every planned animal is chosen there is nothing left to confirm on the Dieren step — go
  // straight on (the extra "Bevestigen" was pure friction). Only while moving FORWARD (step ===
  // furthest), so stepping back to review or remove an animal doesn't bounce you out again.
  useEffect(() => {
    if (locked || currentKey !== "animals" || step !== furthest) return;
    if (selected.length === 0 || selected.length !== plannedCount) return;
    setFurthest(step + 1);
    setStep(step + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, step, furthest, selected.length, plannedCount, locked]);

  // Jmix SurrenderShelterIntakeCostsFragment.setupData calls vulTariefAutomatischIn() when the
  // Afstandskosten step opens: the tariff engine is asked for the surrender fee and a MATCHED result
  // becomes a cost line straight away. "Tarief voorstellen" only REPEATS that by hand — arriving on an
  // empty cost table and having to click it was lost functionality, not the design.
  // Prime the engine the moment the surrender flow has an animal — long before the kosten step opens.
  useEffect(() => {
    if (option !== SURRENDER) return;
    const first = selected[0];
    if (!first) return;
    const sleutel = `${first.id}:${first.species ?? ""}:${first.newAnimal?.geboortedatum ?? ""}`;
    if (tariefGevraagdVoor.current === sleutel) return;
    tariefGevraagdVoor.current = sleutel;
    tariefVoorstelRef.current = null;
    proposeTariff(
      {
        url: "tarief-voorstel", method: "post",
        values: {
          proces: "Afstand", diersoort: first.species ?? null,
          animalId: first.id > 0 ? first.id : null,
          leeftijdMaanden: monthsSince(first.newAnimal?.geboortedatum ?? null),
        },
      },
      {
        // Silent: the answer is only USED when the kosten step opens (the effect below), so a warning
        // here would be about a tariff the operator has not asked for yet.
        onSuccess: ({ data }) => { tariefVoorstelRef.current = data; setTariefTick((n) => n + 1); },
        onError: () => { setTariefTick((n) => n + 1); },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option, selected]);

  useEffect(() => {
    if (currentKey !== "surrenderCosts" || autoTariefDone.current) return;
    if (surrenderCosts.length > 0) { // operator already entered something — don't touch it
      autoTariefDone.current = true;
      return;
    }
    const voorstel = tariefVoorstelRef.current;
    if (voorstel) {
      autoTariefDone.current = true;
      applyTariefVoorstel(voorstel, true);
      return;
    }
    // Still in flight (the operator was quicker than the engine): the tick re-runs this effect the
    // moment the answer lands. Only when there is nothing pending do we ask ourselves.
    if (tariefBusy) return;
    autoTariefDone.current = true;
    proposeSurrenderTariff({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, tariefTick, tariefBusy]);

  // Fetch (debounced) the declaration afdruk whenever a terms step is open and something it embeds
  // changes — the signature, the money, or who signs. "terms" = vindersverklaring (stray/wilde kat),
  // "surrenderTerms"/"returnTerms" = afstandsverklaring, which RETURN reuses exactly as Jmix does.
  const declTemplateKey: "AFSTANDSVERKLARING" | "VINDERSVERKLARING" | null =
    currentKey === "terms" ? "VINDERSVERKLARING"
      : currentKey === "surrenderTerms" || currentKey === "returnTerms" ? "AFSTANDSVERKLARING"
        : null;
  useEffect(() => {
    if (!declTemplateKey) return;
    let cancelled = false;
    setDeclPreviewBezig(true);
    const timer = window.setTimeout(() => {
      fetchWizardDocumentPreview(declTemplateKey, buildValues())
        .then((pages) => {
          if (!cancelled) { setDeclPreviewPages(pages); setDeclPreviewBezig(false); }
        })
        .catch(() => {
          if (!cancelled) { setDeclPreviewPages([]); setDeclPreviewBezig(false); }
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // buildValues reads the whole draft; only re-render for the inputs the document embeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declTemplateKey, strayTerms.signature, surrenderTerms.signature, surrenderTerms.signedLater,
      surrenderCosts, returnRefund, finderId, bringerId]);

  // Per-flow stepper phases, derived from the steps actually present so each intake type shows the
  // right labels (and no empty phase). `phases` = the ordered unique labels; `phaseOf` maps a step key
  // to its index in that list.
  const phaseLabels = stepDefs.map((s) => intakePhaseLabel(s.key, option));
  const phases = phaseLabels.filter((label, i) => phaseLabels.indexOf(label) === i);
  const phaseOf = (key: string) => Math.max(0, phases.indexOf(intakePhaseLabel(key, option)));

  // Mirror the Jmix advance-gates: surrender requires a reason, birth a gemeente, wilde-kat cats-only,
  // and the verklaringen their agreement (+ signature), before leaving the relevant step. The count
  // step requires a choice (+ a valid 2–10 count); the Dieren step must match the chosen count.
  const proceedDisabled =
    (currentKey === "count" && countInvalid) ||
    (currentKey === "type" && !soortId) ||
    (currentKey === "animals" &&
      (selected.length === 0 || (!locked && selected.length !== plannedCount) || wildCatBlocked || animalCountBlocked)) ||
    (currentKey === "ownerConfirm" && ownerConfirmBlocked) ||
    (currentKey === "relationFind" && relationFindBlocked) ||
    // Jmix ExchangeSourceShelterFragment.hasSelection(): no advance without a source shelter.
    (currentKey === "exchangeSource" && bringerId == null) ||
    // "Ja" without naming the ambulance is a half-answer: the stay would record that an ambulance
    // brought the animal while `dieren_ambulance_in_id` stays null. Pick one, or answer "Nee".
    (currentKey === "strayAmbulance" && ambulance.yes && !ambulance.id) ||
    (currentKey === "details" && option === SURRENDER && !surrender.reason) ||
    (currentKey === "details" && option === BIRTH && (!gemeenteId || !intakeDatum)) ||
    // Jmix StrayShelterIntakeFoundLocationFragment: "Kies een gemeente waar het dier is aangetroffen."
    (currentKey === "details" && option === STRAY && !gemeenteId) ||
    // BTW is verplicht op elke kostenregel met een bedrag (0% mag, maar moet gekozen zijn).
    (currentKey === "surrenderCosts" && (surrenderBtwOntbreekt || surrenderKortingTeHoog)) ||
    (currentKey === "surrenderPayment" && surrenderTeveel) ||
    (currentKey === "terms" && !strayTerms.accepted) ||
    (currentKey === "surrenderTerms" && surrenderTermsBlocked) ||
    (currentKey === "returnTerms" && surrenderTermsBlocked) ||
    // The refund is clamped to the "Maximaal" cap (Jmix normalizes the NumberField to [0, max]).
    (currentKey === "returnRestitution" && returnRestitution.selected && returnRefund > returnMaxRefund) ||
    (currentKey === "returnPayment" && (returnPaymentBlocked || returnPayOordeel.fout !== null)) ||
    (currentKey === "chip" && !chipStepValid);

  // wizard-jmix-parity navigation (Jmix planAdvance): Bevestigen on the frontier appends the next
  // card; on an edited earlier step it resumes to the frontier (FOCUS_RESUME) — except the TYPE
  // step, which always clears downstream progress (branch reset), and an edited count that no
  // longer matches the selection, which lands on the Dieren step to fix it.
  const typeIndex = stepDefs.findIndex((s) => s.key === "type");
  const confirmStep = () => {
    if (step >= stepDefs.length - 1) {
      submit();
      return;
    }
    // Verlaat je de betaalstap terwijl er een bedrag blijft openstaan, dan eerst de vraag of dat klopt.
    const betaalOordeel = currentKey === "surrenderPayment" ? surrenderOordeel
      : currentKey === "returnPayment" ? returnPayOordeel : null;
    if (betaalOordeel?.restant && restantVraag == null) {
      setRestantBedrag(betaalOordeel.openstaand);
      setRestantVraag(() => () => doeConfirmStep());
      return;
    }
    doeConfirmStep();
  };
  const doeConfirmStep = () => {
    // Leaving the eigenaar step persists whatever was corrected in its details form (no separate
    // save button — Bevestigen saves, like the Jmix relation step).
    if (currentKey === "relationFind") relationSaveRef.current?.();
    persistDraft();
    if (currentKey === "type") {
      setFurthest(typeIndex + 1);
      setStep(typeIndex + 1);
    } else if (currentKey === "count" && step < furthest && !locked && selected.length !== plannedCount) {
      setStep(1);
    } else if (step < furthest) {
      setStep(furthest);
    } else {
      setFurthest(step + 1);
      setStep(step + 1);
    }
  };
  // Doorstappen na het aanmaken van een relatie. Niet meteen in de callback: de gekozen relatie
  // staat dan nog niet in de state waar de volgende stap en het bewaren van het concept op leunen.
  useEffect(() => {
    if (!naAanmakenDoor || relationId == null) return;
    setNaAanmakenDoor(false);
    confirmStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naAanmakenDoor, relationId]);

  const editStep = (i: number) => {
    persistDraft();
    setStep(i);
  };
  const backStep = () => {
    persistDraft();
    setStep((s) => Math.max(0, s - 1));
  };

  return (
    <div className="fawkes-inboeken">
      {notice && (
        <div className={`fawkes-inboeken__notice fawkes-inboeken__notice--${notice}`} role="alert">
          {t(`inboeken.notice.${notice}`)}
        </div>
      )}
      <WizardModal
        title={t(editMode ? "inboeken.editTitle" : "inboeken.title")}
        subtitle={t(editMode ? "inboeken.editSubtitle" : "inboeken.subtitle")}
        phases={phases}
        steps={stepDefs.map((s) => ({
          ...s,
          // Same rule as the uitboek-wizard: no text recap bands on a completed step — only the animal
          // rows (a node, not a string) stay, because those say WHICH animal you are booking in.
          summary: typeof s.summary === "string" ? undefined : s.summary,
          phase: phaseOf(s.key),
          subtitle: s.key === "count" ? t("wizard.count.sub") : undefined,
        }))}
        active={Math.min(step, stepDefs.length - 1)}
        furthest={Math.min(furthest, stepDefs.length - 1)}
        onEdit={editStep}
        onBack={backStep}
        onConfirm={confirmStep}
        onCancel={onCancel}
        onExpand={onExpand}
        expandLabel={t("wizard.openAsPage")}
        confirmDisabled={proceedDisabled}
        busy={busy}
        confirmLabel={t("wizard.confirm")}
        finishLabel={t(editMode ? "inboeken.editFinish" : "inboeken.finish")}
        backLabel={t("common.back")}
        cancelLabel={t("common.cancel")}
      />
      {/* Een bedrag laten openstaan mag -- een deel nu en de rest later is een gewone afspraak --
          maar niet per ongeluk. Daarom geen blokkade maar een vraag, met het bedrag erin zodat je
          ziet waar je ja tegen zegt (Maarten, 2026-10-24). */}
      <Modal
        open={restantVraag != null}
        title={t("betaling.restantTitel")}
        onClose={() => setRestantVraag(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRestantVraag(null)}>
              {t("betaling.restantNee")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const verder = restantVraag;
                setRestantVraag(null);
                verder?.();
              }}
            >
              {t("betaling.restantJa")}
            </Button>
          </>
        }
      >
        <p>{t("betaling.restantVraag", { args: [formatEuro(Math.abs(restantBedrag), locale)] })}</p>
      </Modal>
      <ProductTariefPicker
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onPick={addSurrenderCostFromPicker}
        t={t}
        locale={locale}
      />
    </div>
  );

  // ── step bodies ────────────────────────────────────────────────────────────────────────────────

  function renderCount() {
    // Jmix SelectAnimalCountFragment: Één dier / Meerdere dieren as 20em illustrated cards
    // (green-filtered paw/kittens SVGs), the count field INSIDE the Meerdere card, and a GREEN
    // selected accent (unlike the gold type cards). The Meerdere card is a div[role=button] so the
    // nested count input remains valid, interactive markup.
    return (
      <div className="fawkes-inboeken__step">
        <div className="fawkes-om-modal__cards">
          <button
            type="button"
            aria-pressed={multi === false}
            className={"fawkes-om-modal__card" + (multi === false ? " fawkes-om-modal__card--selected" : "")}
            onClick={() => setMulti(false)}
          >
            <span className="fawkes-om-modal__radio">
              {multi === false && <span className="fawkes-om-modal__radio-dot" />}
            </span>
            <span className="fawkes-om-modal__card-media">
              <PawIllustration />
            </span>
            <span className="fawkes-om-modal__card-title">{t("wizard.count.single")}</span>
            <span className="fawkes-om-modal__card-desc">{t("inboeken.count.singleDesc")}</span>
          </button>
          <div
            role="button"
            tabIndex={0}
            aria-pressed={multi === true}
            className={"fawkes-om-modal__card" + (multi === true ? " fawkes-om-modal__card--selected" : "")}
            onClick={() => setMulti(true)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                e.preventDefault();
                setMulti(true);
              }
            }}
          >
            <span className="fawkes-om-modal__radio">
              {multi === true && <span className="fawkes-om-modal__radio-dot" />}
            </span>
            <span className="fawkes-om-modal__card-media">
              <KittensIllustration />
            </span>
            <span className="fawkes-om-modal__card-title">{t("wizard.count.multiple")}</span>
            <span className="fawkes-om-modal__card-desc">{t("inboeken.count.multiDesc")}</span>
            {/* The count field is always shown so the operator can click straight into it; focusing or
                typing also picks the "Meerdere dieren" card (no separate card click needed). */}
            <label className="fawkes-om-modal__count-field" onClick={(e) => e.stopPropagation()}>
              <span className="fawkes-om-modal__count-label">{t("wizard.count.amount")}</span>
              <input
                className="fawkes-om-modal__count-input"
                type="number"
                aria-label={t("wizard.count.amount")}
                min={2}
                max={MAX_ANIMAL_COUNT}
                value={countInput}
                onFocus={() => setMulti(true)}
                onChange={(e) => {
                  setMulti(true);
                  setCountInput(e.target.value);
                }}
              />
              <span className="fawkes-inboeken__helper">{t("inboeken.animals.countRange")}</span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  function renderType() {
    // Icon type-cards (Phase 9), mirroring the Jmix ShelterIntakeTypeSelectionFragment grid: one
    // selectable card per curated allow-list type (icon + title + description), Uitwisseling /
    // Tijdelijke opvang / Overig greyed (alwaysDisabled → "tijdelijk niet beschikbaar"). Wilde-kat
    // greys out when a selected animal is not a cat (animals are chosen BEFORE the type now — the
    // same contextual disable the Jmix grid shows).
    return (
      <div className="fawkes-inboeken__step">
        <div className="fawkes-om-modal__cards fawkes-om-modal__cards--compact">
          {soorten.map((s) => {
            const key = typeKey(s);
            const descKey = TYPE_DESC_KEY[key];
            const active = String(s.id) === soortId;
            const wildcatDisabled = key === "wildcat" && wildCatIncompatible;
            const cardDisabled = s.alwaysDisabled || wildcatDisabled;
            return (
              <button
                type="button"
                key={s.id}
                disabled={cardDisabled}
                aria-pressed={active}
                title={
                  s.alwaysDisabled
                    ? t("inboeken.soort.unavailable")
                    : wildcatDisabled
                      ? t("inboeken.type.wildcatCatsOnly")
                      : undefined
                }
                className={
                  "fawkes-om-modal__card" +
                  (active ? " fawkes-om-modal__card--selected" : "") +
                  (cardDisabled ? " fawkes-om-modal__card--disabled" : "")
                }
                onClick={() => {
                  if (s.alwaysDisabled) return;
                  setSoortId(String(s.id));
                  resetDetails();
                }}
              >
                <span className="fawkes-om-modal__radio">
                  {active && <span className="fawkes-om-modal__radio-dot" />}
                </span>
                <span className="fawkes-om-modal__card-media">
                  <img className="fawkes-inboeken__type-icon" src={TYPE_ICON[key] ?? TYPE_ICON.other} alt="" />
                </span>
                <span className="fawkes-om-modal__card-title">{s.label}</span>
                {descKey && <span className="fawkes-om-modal__card-desc">{t(descKey)}</span>}
                {s.alwaysDisabled && (
                  <span className="fawkes-inboeken__type-unavailable">{t("inboeken.soort.unavailableShort")}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function renderAnimals() {
    // Jmix ShelterIntakeAddAnimalsFragment: per-animal Search → Results → Details loop until the
    // planned count is reached; the confirmed animals list with photo rows + per-row Verwijderen.
    const roomLeft = animalSubwizardActive;
    return (
      <div className="fawkes-inboeken__step">
        {wildCatBlocked && (
          <div className="fawkes-inboeken__field-error" role="alert">{t("inboeken.type.wildcatCatsOnly")}</div>
        )}
        {animalCountBlocked && (
          <div className="fawkes-inboeken__field-error" role="alert">{t("inboeken.animals.countRange")}</div>
        )}
        <ul className="fawkes-inboeken__selected">
          {selected.map((a) => (
            <li key={a.id} className="fawkes-animalsub__selected-row">
              <AnimalThumb fotoUrl={photoSrc(a)} alt={a.naam} noPhotoLabel={t("inboeken.results.noPhoto")} />
              <span className="fawkes-stepwiz__animal-row-label">
                {animalRowText({ chip: a.chip, naam: a.naam, soort: a.species, ras: a.ras, geslacht: a.geslacht, kleur: a.kleur, vacht: a.vacht })}
              </span>
              {!locked && (
                <button
                  type="button"
                  className="fawkes-inboeken__link"
                  onClick={() => setSelected((cur) => cur.filter((x) => x.id !== a.id))}
                >
                  {t("inboeken.animals.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
        {roomLeft && (
          <AnimalSubwizard
            key={selected.length}
            animals={animals}
            residentIds={residentSet}
            selectedIds={selectedIdSet}
            onAddExisting={addExistingAnimal}
            onAddNew={addNewAnimal}
          />
        )}
      </div>
    );
  }

  /**
   * The relation (eigenaar) search step — the Jmix relation sub-wizard: search an existing relation by
   * e-mail and/or address, pick one from the results, or create a new one with the searched address
   * prefilled. Once picked it shows the chosen relation with a "Wijzigen" escape hatch.
   */
  // Jmix `strayAmbulance` — its own step between the type and the found location, not a toggle tucked
  // into the Herkomst card. Two full choice cards; picking "Ja" reveals the ambulance + ritnummer
  // fields inside that card, exactly like the other card-style choices in this wizard.
  function renderStrayAmbulance() {
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("inboeken.ambulance.question")} info={t("inboeken.ambulance.info")} />
        <div className="fawkes-om-modal__cards">
          <div
            role="button"
            tabIndex={0}
            className={`fawkes-om-modal__card fawkes-inboeken__ambulance-card${ambulance.yes ? " fawkes-om-modal__card--selected" : ""}`}
            // The fields sit inside this card and cover much of it. Once "Ja" is active they own their
            // own clicks; while it is not, a click anywhere in the card — fields included — picks it.
            onClick={(e) => {
              if (ambulance.yes && (e.target as HTMLElement).closest(".fawkes-inboeken__ambulance-fields")) return;
              setAmbulance((a) => ({ ...a, yes: true }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setAmbulance((a) => ({ ...a, yes: true }));
              }
            }}
          >
            <span className="fawkes-om-modal__card-media">
              <img className="fawkes-inboeken__type-icon" src="/intake/icon_dierenambulance.svg" alt="" />
            </span>
            <span className="fawkes-om-modal__card-title">{t("inboeken.ambulance.yes")}</span>
            <span className="fawkes-om-modal__card-desc">{t("inboeken.ambulance.yes.desc")}</span>
            {/* Jmix keeps these visible but disabled while "Nee" is chosen, so the card doesn't
                change height when you switch — matched here. Clicks inside must not re-fire the
                card's own onClick. */}
            <div
              className="fawkes-inboeken__ambulance-fields"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {/* Type-to-search, like Diersoort/Ras — a plain <select> made you hunt through the list. */}
              <ComboBox
                label={t("inboeken.field.dierenambulance")}
                value={ambulance.id}
                disabled={!ambulance.yes}
                invalid={ambulance.yes && !ambulance.id}
                placeholder={t("inboeken.field.dierenambulance.placeholder")}
                onChange={(v) => setAmbulance((a) => ({ ...a, id: v }))}
                options={dierenambulances.map((d) => ({ value: String(d.id), label: d.label }))}
              />
              {/* Say why Bevestigen is dead rather than leaving the operator guessing. */}
              {ambulance.yes && !ambulance.id && (
                <span className="fawkes-inboeken__field-error">
                  {t("inboeken.field.dierenambulance.required")}
                </span>
              )}
              <Field label={t("inboeken.field.ritnummer")} value={ambulance.ride}
                disabled={!ambulance.yes}
                onChange={(e) => setAmbulance((a) => ({ ...a, ride: e.target.value }))} />
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className={`fawkes-om-modal__card fawkes-inboeken__ambulance-card${!ambulance.yes ? " fawkes-om-modal__card--selected" : ""}`}
            onClick={() => setAmbulance({ yes: false, id: "", ride: "" })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setAmbulance({ yes: false, id: "", ride: "" });
              }
            }}
          >
            <span className="fawkes-om-modal__card-media">
              <PawIllustration />
            </span>
            <span className="fawkes-om-modal__card-title">{t("inboeken.ambulance.no")}</span>
            <span className="fawkes-om-modal__card-desc">{t("inboeken.ambulance.no.desc")}</span>
          </div>
        </div>
      </div>
    );
  }

  function renderRelationFind() {
    // The picker owns every phase here — search, results, new relation, and (once chosen) the
    // editable owner details, so the operator can correct/complete them without leaving the wizard.
    return (
      <div className="fawkes-inboeken__step">
        <RelationPicker
          variant="step"
          label={t(relationLabel)}
          stepTitle={t(option === STRAY ? "inboeken.relation.findFinderTitle" : "inboeken.relation.findTitle")}
          stepInfo={t(
            option === STRAY
              ? "inboeken.relation.findInfo.stray"
              : option === RETURN
                ? "inboeken.relation.findInfo.return"
                : "inboeken.relation.findInfo.surrender",
          )}
          valueId={relationId}
          valueLabel={relationName}
          onChange={setRelation}
          canCreate={canCreateRelatie}
          feedPath="intake/reference/klanten"
          // A stray finder is optional — the explicit escape Jmix offers on this screen.
          noneLabel={option === STRAY ? t("inboeken.relation.noFinder") : undefined}
          // "Geen vinder" is the escape off this step, so it has to MOVE you off it. It only set the
          // flag before: the card owns the footer here (hideActions), so clicking it cleared the
          // relation and then left the operator on the same screen with no way forward.
          onNone={() => {
            setFinderNone(true);
            persistDraft();
            setFurthest((f) => Math.max(f, step + 1));
            setStep((s) => s + 1);
          }}
          // Jmix puts Terug / Geen vinder / Zoeken in this card's own footer, so the search phase
          // owns its navigation and the modal footer steps aside (see hideActions below).
          onBack={() => setStep((s) => Math.max(0, s - 1))}
          onPhaseChange={setRelationPhase}
          registerSave={registerRelationSave}
          onCreated={() => setNaAanmakenDoor(true)}
          t={t}
        />
      </div>
    );
  }

  function renderOwnerConfirm() {
    // Shared Jmix fragment (ReturnAfterPlacementOwnerConfirmationFragment): RETURN asks "Is dit de
    // correcte eigenaar?" under the "Retour na plaatsing" header; SURRENDER mode has its own pair.
    const isReturn = option === RETURN;
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t(isReturn ? "inboeken.returnOwnerConfirm.title" : "inboeken.ownerConfirm.title")} />
        {knownOwner ? (
          <OwnerConfirmCard
            owner={knownOwner}
            choice={ownerConfirmChoice}
            onYes={confirmOwnerYes}
            onNo={confirmOwnerNo}
            question={t(isReturn ? "inboeken.returnOwnerConfirm.question" : "inboeken.ownerConfirm.question")}
            yesLabel={t("inboeken.ownerConfirm.yes")}
            noLabel={t("inboeken.ownerConfirm.no")}
            t={t}
          />
        ) : knownOwnerQuery.isLoading ? null : (
          // Vangnet: de stap valt weg zodra de opzoeking niets oplevert, dus dit hoort niemand te zien.
          <span className="fawkes-inboeken__helper">{t("inboeken.ownerConfirm.none")}</span>
        )}
      </div>
    );
  }

  function renderExchangeSource() {
    // Jmix ExchangeSourceShelterFragment (the manual exchange intake's Herkomst-asiel step): search
    // over the ASIEL-eligible shelters by name OR city, contact-info cards (E-mail / Telefoon /
    // Adres — missing data renders "-" so every card carries the same rows), click selects the
    // shelter as the brenger. The date field is the React wizard's usual "Datum binnenkomst" (every
    // flow carries it — audit §3 row 7); Jmix has no date input (opnameDatum = now).
    const needle = shelterQuery.trim().toLowerCase();
    const visible = exchangeShelters.filter(
      (s) =>
        !needle ||
        (s.naam ?? "").toLowerCase().includes(needle) ||
        (s.woonplaats ?? "").toLowerCase().includes(needle),
    );
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("inboeken.exchangeSource.title")} info={t("inboeken.exchangeSource.info")} />
        <Field
          label={t("inboeken.field.datum")}
          type="date"
          value={intakeDatum}
          onChange={(e) => setIntakeDatum(e.target.value)}
        />
        <input
          className="fui-field__input"
          value={shelterQuery}
          placeholder={t("inboeken.exchangeSource.search")}
          onChange={(e) => setShelterQuery(e.target.value)}
        />
        <ul className="fawkes-inboeken__results fawkes-exchange-party">
          {visible.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                aria-pressed={bringerId === s.id}
                className={bringerId === s.id ? "is-selected" : ""}
                onClick={() => setRelation(s.id, s.naam)}
              >
                <span className="fawkes-exchange-party__name">{s.naam ?? "-"}</span>
                <span className="fawkes-exchange-party__meta">
                  {t("inboeken.exchangeSource.card.email")}: {s.email || "-"}
                </span>
                <span className="fawkes-exchange-party__meta">
                  {t("inboeken.exchangeSource.card.phone")}: {s.telefoon || "-"}
                </span>
                <span className="fawkes-exchange-party__meta">
                  {t("inboeken.exchangeSource.card.address")}: {s.adres || "-"}
                </span>
              </button>
            </li>
          ))}
          {visible.length === 0 && (
            <li className="fawkes-inboeken__muted">
              {t(
                exchangeShelters.length === 0
                  ? "inboeken.exchangeSource.empty"
                  : "inboeken.exchangeSource.noMatches",
              )}
            </li>
          )}
        </ul>
      </div>
    );
  }

  function renderDetails() {
    const motherNeedle = motherQuery.trim().toLowerCase();
    const filteredMothers = animals
      .filter(
        (a) =>
          !motherNeedle ||
          a.naam.toLowerCase().includes(motherNeedle) ||
          (a.chipPasnummer ?? "").toLowerCase().includes(motherNeedle),
      )
      .slice(0, 10);
    return (
      <div className="fawkes-inboeken__step fawkes-inboeken__grid">
        {option === STRAY && (
          <div className="fawkes-inboeken__field--full">
            <StepIntro title={t("inboeken.foundLocation.title")} info={t("inboeken.foundLocation.info")} />
          </div>
        )}
        {option === BIRTH ? (
          // Jmix birth-details: the single date field is the animal's Geboortedatum, and the intake
          // date is set equal to it ("De binnenkomstdatum wordt gelijkgesteld aan de geboortedatum.").
          <div className="fawkes-inboeken__field">
            <Field
              label={t("inboeken.field.geboortedatum")}
              type="date"
              value={intakeDatum}
              onChange={(e) => setIntakeDatum(e.target.value)}
            />
            <span className="fawkes-inboeken__helper">{t("inboeken.birth.dateInfo")}</span>
            {!intakeDatum && (
              <span className="fawkes-inboeken__field-error">{t("inboeken.field.geboortedatum")}</span>
            )}
          </div>
        ) : option === STRAY ? (
          // Jmix's Vindplaats screen asks no intake date at all (verified on acc) — it stays at
          // today's date behind the scenes, so the step is only about WHERE the animal was found.
          null
        ) : (
          <Field
            label={t("inboeken.field.datum")}
            type="date"
            value={intakeDatum}
            onChange={(e) => setIntakeDatum(e.target.value)}
          />
        )}
        {option === SURRENDER || option === RETURN || option === STRAY ? (
          // The owner lives on its own step (ownerConfirm → eigenaar zoeken/bewerken); Jmix's Reden
          // afstand card carries only the reason + remarks, so nothing about the owner renders here.
          // STRAY likewise sends its finder to the relationFind step.
          null
        ) : (
          <RelationPicker
            label={t(relationLabel)}
            valueId={relationId}
            valueLabel={relationName}
            onChange={setRelation}
            canCreate={canCreateRelatie}
            feedPath="intake/reference/klanten"
            noneLabel={option === STRAY ? t("inboeken.relation.noFinder") : undefined}
            t={t}
          />
        )}
        {option === STRAY && (
          <>
            {/* Jmix found-location order: Gemeente → Woonplaats → Postcode → Huisnummer → Toevoeging → Straat → Opmerking */}
            <div className="fawkes-inboeken__field">
              <ComboBox
                label={t("inboeken.field.gemeente")}
                value={gemeenteId}
                onChange={setGemeenteId}
                placeholder={t("inboeken.field.gemeente.placeholder")}
                invalid={!gemeenteId}
                options={gemeenten.map((g) => ({ value: String(g.id), label: g.label }))}
              />
              {!gemeenteId && (
                <span className="fawkes-inboeken__field-error">{t("inboeken.field.gemeente.strayRequired")}</span>
              )}
            </div>
            {/* Jmix couples Woonplaats to Gemeente: it is a dropdown of that municipality's own towns
                (gemeente.woonplaatsen), not a free-text box. A reverse-geocoded city that isn't in the
                list is still kept as a value so a map pin never silently loses its town. */}
            <ComboBox
              label={t("inboeken.field.woonplaats")}
              value={stray.city}
              onChange={(v) => setStray((s) => ({ ...s, city: v }))}
              placeholder={t("inboeken.field.woonplaats.placeholder")}
              options={woonplaatsOptions}
            />
            <Field label={t("inboeken.field.postcode")} value={stray.postalCode}
              onChange={(e) => setStray((s) => ({ ...s, postalCode: e.target.value }))} />
            <Field label={t("inboeken.field.huisnummer")} value={stray.houseNumber}
              onChange={(e) => setStray((s) => ({ ...s, houseNumber: e.target.value }))} />
            <Field label={t("inboeken.field.toevoeging")} value={stray.houseNumberAddition}
              onChange={(e) => setStray((s) => ({ ...s, houseNumberAddition: e.target.value }))} />
            <Field label={t("inboeken.field.straat")} value={stray.street}
              onChange={(e) => setStray((s) => ({ ...s, street: e.target.value }))} />
            {/* Achtergelaten ("gedumpt") — een eigen vlag op het verblijf; het overzichtsfilter Gedumpt leest hem. */}
            <div className="fawkes-inboeken__field--full">
              <ToggleField
                label={t("inboeken.field.gedumpt")}
                checked={stray.dumped}
                onChange={(e) => setStray((s) => ({ ...s, dumped: e.target.checked }))}
              />
            </div>
            {/* A free-text note about where the animal was found — its own full-width row, 3 rows high. */}
            <div className="fawkes-inboeken__field--full">
              <TextareaField
                label={t("inboeken.field.opmerkingVindplaats")}
                value={stray.remark}
                rows={3}
                onChange={(e) => setStray((s) => ({ ...s, remark: e.target.value }))}
              />
            </div>
            <div className="fawkes-inboeken__map-block">
              <span className="fui-field__label">{t("inboeken.map.label")}</span>
              <span className="fawkes-inboeken__helper">{t("inboeken.map.info")}</span>
              <FoundLocationMap
                lat={carry.lat}
                lng={carry.lng}
                onPick={handleStrayPick}
                defaultCenter={strayMapCenter}
              />
            </div>
          </>
        )}
        {option === SURRENDER && (
          <>
            {/* Jmix "Reden afstand": a searchable combobox (clear + chevron) and a real Opmerkingen
                textarea with its explanatory placeholder — not a plain select + one-line input. */}
            <div className="fawkes-inboeken__field">
              <ComboBox
                label={t("inboeken.field.redenAfstand")}
                value={surrender.reason}
                onChange={(v) => setSurrender((s) => ({ ...s, reason: v }))}
                placeholder={t("inboeken.field.redenAfstand.placeholder")}
                invalid={!surrender.reason}
                options={SURRENDER_REASONS.map((r) => ({ value: r, label: r }))}
              />
              {!surrender.reason && (
                <span className="fawkes-inboeken__field-error">{t("inboeken.field.redenAfstand.required")}</span>
              )}
            </div>
            {/* Opmerkingen gets its own full-width row under Datum binnenkomst + Reden afstand — a
                one-column-wide box is too cramped for a free-text explanation. */}
            <div className="fawkes-inboeken__field--full">
              <TextareaField
                label={t("inboeken.field.opmerkingen")}
                value={surrender.remark}
                placeholder={t("inboeken.field.redenAfstand.remarkPlaceholder")}
                rows={5}
                onChange={(e) => setSurrender((s) => ({ ...s, remark: e.target.value }))}
              />
            </div>
          </>
        )}
        {/* RETURN (new intake): the reason (fixed vocabulary) + remark live on the restitution step,
            like the Jmix ReturnRestitutionFragment — the details card only carries the date + owner.
            Edit mode has no restitution step, so the same vocabulary select renders here. */}
        {option === RETURN && editMode && (
          <>
            {renderReturnReasonSelect()}
            <Field label={t("inboeken.field.opmerking")} value={returnFields.remark}
              onChange={(e) => setReturnFields((s) => ({ ...s, remark: e.target.value }))} />
          </>
        )}
        {option === BIRTH && (
          <>
            <div className="fawkes-inboeken__field">
              <span className="fui-field__label">{t("inboeken.field.moederdier")}</span>
              {carry.motherId != null ? (
                <div className="fawkes-inboeken__mother-card">
                  <span>{birth.motherName || `#${carry.motherId}`}</span>
                  <button
                    type="button"
                    className="fawkes-inboeken__link"
                    onClick={() => {
                      setBirth((s) => ({ ...s, motherName: "" }));
                      setCarry((c) => ({ ...c, motherId: null }));
                    }}
                  >
                    {t("inboeken.relation.clear")}
                  </button>
                </div>
              ) : (
                <>
                  <input
                    className="fui-field__input"
                    value={motherQuery}
                    placeholder={t("inboeken.mother.search")}
                    onChange={(e) => setMotherQuery(e.target.value)}
                  />
                  {motherNeedle && (
                    <ul className="fawkes-inboeken__results">
                      {filteredMothers.map((a) => (
                        <li key={a.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setBirth((s) => ({ ...s, motherName: a.naam }));
                              setCarry((c) => ({ ...c, motherId: a.id }));
                              setMotherQuery("");
                            }}
                          >
                            {animalLabel(a)}
                          </button>
                        </li>
                      ))}
                      {filteredMothers.length === 0 && (
                        <li className="fawkes-inboeken__muted">{t("inboeken.mother.notFound")}</li>
                      )}
                    </ul>
                  )}
                </>
              )}
            </div>
            <div className="fawkes-inboeken__field">
              <ComboBox
                label={t("inboeken.field.gemeente")}
                value={gemeenteId}
                onChange={setGemeenteId}
                placeholder={t("inboeken.field.gemeente.birthPlaceholder")}
                invalid={!gemeenteId}
                options={gemeenten.map((g) => ({ value: String(g.id), label: g.label }))}
              />
              <span className="fawkes-inboeken__helper">{t("inboeken.field.gemeente.birthHelper")}</span>
              {!gemeenteId && (
                <span className="fawkes-inboeken__field-error">{t("inboeken.field.gemeente.required")}</span>
              )}
            </div>
            <Field label={t("inboeken.field.opmerking")} value={birth.remark}
              onChange={(e) => setBirth((s) => ({ ...s, remark: e.target.value }))} />
          </>
        )}
      </div>
    );
  }

  /**
   * Print the declaration the operator is looking at: the SERVER-rendered document, the same one the
   * step previews and the archiver stores on completion. "Print verklaring" used to open a locally
   * composed HTML page instead, so the printout was a different document from the one on screen (and
   * from the one in the dossier). Works before signing too — printing an unsigned declaration to have
   * it signed on paper is exactly why the button exists.
   */
  function doPrintDeclaration(templateKey: "AFSTANDSVERKLARING" | "VINDERSVERKLARING") {
    setDeclPrintError(null);
    setDeclPrintBezig(true);
    downloadWizardDocumentPdf(templateKey, buildValues())
      .catch(() => setDeclPrintError(t("inboeken.terms.printFailed")))
      .finally(() => setDeclPrintBezig(false));
  }

  // Same screen as the afstandsverklaring, minus the money — a finder owes nothing (maintainer:
  // "moet lijken op die van afstand maar dan zonder de kosten").
  function renderTerms() {
    return renderDeclaration({
      header: t("inboeken.terms.header"),
      info: t("inboeken.terms.info"),
      contactTag: t("inboeken.terms.tag.vinder"),
      declTitle: t("inboeken.terms.declTitle"),
      declIntro: t("inboeken.terms.declIntro"),
      declText: t("inboeken.terms.declText"),
      agreeLabel: t("inboeken.terms.agree"),
      // Jmix's vindersverklaring has no "tekent later" escape — the finder is present.
      signLaterLabel: null,
      accepted: strayTerms.accepted,
      onAccepted: (v) => setStrayTerms((s) => ({ ...s, accepted: v })),
      signedLater: false,
      onSignedLater: () => {},
      signature: strayTerms.signature,
      onSignature: (d) => setStrayTerms((s) => ({ ...s, signature: d })),
      date: strayTerms.date,
      onDate: (v) => setStrayTerms((s) => ({ ...s, date: v })),
      place: strayTerms.place,
      onPlace: (v) => setStrayTerms((s) => ({ ...s, place: v })),
      error: strayTerms.accepted ? null : t("inboeken.terms.agreeRequired"),
      money: null,
      onPrint: () => doPrintDeclaration("VINDERSVERKLARING"),
    });
  }

  function renderSurrenderCosts() {
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("inboeken.costs.header")} info={t("inboeken.costs.info")} />
        <div className="fawkes-inboeken__tarief-propose">
          <button
            type="button"
            className="fawkes-inboeken__link"
            onClick={() => proposeSurrenderTariff()}
            disabled={tariefBusy}
          >
            ✨ {t("inboeken.costs.propose")}
          </button>
          {tariefNotice && (
            <span
              className={
                tariefNotice.type === "ok"
                  ? "fawkes-inboeken__helper"
                  : "fawkes-inboeken__muted"
              }
              role="status"
            >
              {tariefNotice.text}
            </span>
          )}
        </div>
        <ul className="fawkes-inboeken__cost-lines">
          {surrenderCosts.map((c) => (
            <li key={c.id} className="fawkes-inboeken__cost-line">
              <input
                className="fui-field__input"
                value={c.description}
                placeholder={t("inboeken.costs.product")}
                onChange={(e) =>
                  setSurrenderCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, description: e.target.value } : x)))
                }
              />
              <input
                className="fui-field__input fawkes-inboeken__cost-amount"
                type="text"
                inputMode="decimal"
                value={c.amount}
                placeholder={t("inboeken.costs.amount.placeholder")}
                onChange={(e) =>
                  setSurrenderCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, amount: e.target.value } : x)))
                }
              />
              {/* Korting per regel: de btw van de korting is dan vanzelf die van de regel, en je
                  kunt op het ene product wel korting geven en op het andere niet. */}
              <input
                className="fui-field__input fawkes-inboeken__cost-korting"
                type="text"
                inputMode="decimal"
                value={c.korting ?? ""}
                placeholder={t("korting.placeholder")}
                aria-label={t("korting.label")}
                title={t("korting.label")}
                onChange={(e) =>
                  setSurrenderCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, korting: e.target.value } : x)))
                }
              />
              <KostenregelBtw
                value={c.btw}
                onChange={(btw) => setSurrenderCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, btw } : x)))}
                percentages={btwPercentages}
                missing={parseAmount(c.amount) != null && c.btw == null}
                t={t}
              />
              <button
                type="button"
                className="fawkes-inboeken__link"
                onClick={() => setSurrenderCosts((cur) => cur.filter((x) => x.id !== c.id))}
              >
                {t("inboeken.animals.remove")}
              </button>
            </li>
          ))}
          {surrenderCosts.length === 0 && (
            <li className="fawkes-inboeken__muted">{t("inboeken.costs.empty")}</li>
          )}
        </ul>
        {surrenderBtwOntbreekt && (
          <div className="fawkes-inboeken__field-error" role="alert">{t("btw.verplicht")}</div>
        )}
        <button
          type="button"
          className="fawkes-inboeken__link"
          onClick={() => setProductPickerOpen(true)}
        >
          {t("inboeken.costs.add")}
        </button>
        <div className="fawkes-inboeken__cost-total fawkes-inboeken__cost-total--grand">
          <span className="fui-field__label">{t("inboeken.costs.total")}</span>
          <strong>{formatEuro(surrenderCostTotal, locale)}</strong>
        </div>
      </div>
    );
  }

  // A picked catalog line for the surrender costs: aantal × prijs collapses into one line amount
  // (Jmix SurrenderShelterIntakeCostsFragment consumes the picker the same way).
  function addSurrenderCostFromPicker(regel: GekozenProductRegel) {
    setSurrenderCosts((cur) => [
      ...cur,
      {
        id: costSeq,
        description: regel.omschrijving,
        amount: (regel.aantal * regel.prijsInclBtw).toFixed(2),
        btw: regel.btwPercentage,
      },
    ]);
    setCostSeq((n) => n + 1);
  }

  // Shared by the SURRENDER Voorwaarden step and the RETURN one (Jmix STEP_RETURN_TERMS reuses the
  // SurrenderShelterIntakeTermsAgreementFragment verbatim) — only the relation differs.
  /**
   * The shared declaration screen behind Afstandsverklaring / Retour / Vindersverklaring, laid out the
   * way the running Jmix does it (walked on acc): two tagged cards (the person's details next to the
   * animal's, the animal card carrying the money total), the declaration as a NUMBERED list with the
   * cost lines folded into the payment clause, an accept toggle with the "tekent later" escape beside
   * it, and the signature pad next to a Datum/Plaats block.
   */
  function renderDeclaration(opts: {
    header: string;
    info: string;
    contactTag: string;
    declTitle: string;
    declIntro: string;
    declText: string;
    agreeLabel: string;
    signLaterLabel: string | null;
    accepted: boolean;
    onAccepted: (v: boolean) => void;
    signedLater: boolean;
    onSignedLater: (v: boolean) => void;
    signature: string;
    onSignature: (v: string) => void;
    date: string;
    onDate: (v: string) => void;
    place: string;
    onPlace: (v: string) => void;
    error: string | null;
    /** Cost/refund lines + total folded into the declaration; omitted for the vindersverklaring. */
    money: { label: string; total: string; lines: { description: string; amount: string }[] } | null;
    onPrint: () => void;
  }) {
    const animal = selected[0];
    // The declText carries its own "1. …\n2. …" numbering; an <ol> renumbers, so strip the prefix.
    const clauses = opts.declText
      .split("\n")
      .map((line) => line.replace(/^\s*\d+\.\s*/, "").trim())
      .filter(Boolean);
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={opts.header} info={opts.info} />
        <div className="fawkes-terms__cards">
          <section className="fawkes-terms__card">
            <span className="fawkes-terms__tag">{opts.contactTag}</span>
            <dl className="fawkes-terms__facts">
              <div><dt>{t("inboeken.terms.fact.naam")}</dt><dd>{termsKlant?.naam ?? "—"}</dd></div>
              <div><dt>{t("inboeken.terms.fact.adres")}</dt><dd>{addressLine(termsKlant) || "—"}</dd></div>
              <div>
                <dt>{t("inboeken.terms.fact.telefoon")}</dt>
                <dd>{[termsKlant?.telefoon, termsKlant?.mobiel].filter(Boolean).join(" / ") || "—"}</dd>
              </div>
              <div><dt>{t("inboeken.terms.fact.email")}</dt><dd>{termsKlant?.email ?? "—"}</dd></div>
            </dl>
          </section>
          <section className="fawkes-terms__card">
            <span className="fawkes-terms__tag">{t("inboeken.summary.section.dier")}</span>
            <dl className="fawkes-terms__facts">
              <div><dt>{t("inboeken.terms.fact.diernaam")}</dt><dd>{animal?.naam || animal?.label || "—"}</dd></div>
              <div><dt>{t("inboeken.terms.fact.chipnummer")}</dt><dd>{animal?.chip || "—"}</dd></div>
              <div>
                <dt>{t("inboeken.terms.fact.soortRas")}</dt>
                <dd>{[animal?.species, animal?.ras].filter(Boolean).join(" - ") || "—"}</dd>
              </div>
            </dl>
            {opts.money && (
              <div className="fawkes-terms__total">
                <span>{opts.money.label}</span>
                <strong>{opts.money.total}</strong>
              </div>
            )}
          </section>
        </div>
        <section className="fawkes-terms__declaration">
          <span className="fawkes-terms__tag">{opts.declTitle}</span>
          {declPreviewPages.length === 0 && declPreviewBezig ? (
            <p className="fawkes-terms__intro">{t("common.loading")}</p>
          ) : declPreviewPages.length > 0 ? (
            /* The real afdruk — the same COMM_TEMPLATE document the completion archives, with the
               live signature embedded server-side. */
            <div className="fawkes-inboeken__terms-preview">
              {declPreviewPages.map((src, i) => (
                <img
                  key={i}
                  className="fawkes-inboeken__terms-preview-page"
                  src={src}
                  alt={`${opts.declTitle} ${i + 1}`}
                />
              ))}
            </div>
          ) : (
          <>
          <p className="fawkes-terms__intro">{opts.declIntro}</p>
          <ol className="fawkes-terms__clauses">
            {clauses.map((clause, i) => (
              <li key={i}>
                {clause}
                {/* Jmix prints the cost breakdown inside the clause that agrees to pay them. */}
                {opts.money && i === clauses.length - 1 && (
                  <table className="fawkes-terms__cost-table">
                    <tbody>
                      {opts.money.lines.map((line, j) => (
                        <tr key={j}>
                          <td>{line.description}</td>
                          <td>{line.amount}</td>
                        </tr>
                      ))}
                      <tr className="fawkes-terms__cost-total-row">
                        <td>{opts.money.label}</td>
                        <td>{opts.money.total}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ol>
          </>
          )}
        </section>
        <div className="fawkes-terms__agree-row">
          <label className="fawkes-terms__toggle">
            <input
              type="checkbox"
              role="switch"
              checked={opts.accepted}
              onChange={(e) => opts.onAccepted(e.target.checked)}
            />
            {/* Some of these labels already end in the required-marker, so don't add a second one. */}
            <span>{opts.agreeLabel.trim().endsWith("*") ? opts.agreeLabel : `${opts.agreeLabel} *`}</span>
          </label>
          {opts.signLaterLabel && (
            <Button
              variant={opts.signedLater ? "primary" : "secondary"}
              onClick={() => opts.onSignedLater(!opts.signedLater)}
            >
              {opts.signLaterLabel}
            </Button>
          )}
        </div>
        {opts.error && <span className="fawkes-inboeken__field-error">{opts.error}</span>}
        <div className="fawkes-terms__sign-row">
          {/* The label sits in its own grid row, so the signature card and the facts card next to it
              start on the same line and (row 2 stretches) end on the same line. */}
          <span className="fui-field__label fawkes-terms__sign-label">
            {t("inboeken.terms.signature")}
          </span>
          <div className="fawkes-terms__sign">
            {opts.signedLater ? (
              <p className="fawkes-inboeken__muted">{opts.signLaterLabel}</p>
            ) : (
              <SignaturePad
                value={opts.signature}
                onChange={opts.onSignature}
                retryLabel={t("inboeken.terms.signatureRetry")}
                placeholder={t("inboeken.terms.signaturePlaceholder")}
              />
            )}
          </div>
          {/* Jmix/Design print these as facts beside the signature, not as inputs — the date is the
              intake date and the place is the branch. The money total closes the block, except on the
              vindersverklaring: a finder owes nothing. */}
          <aside className="fawkes-terms__meta">
            <p className="fawkes-terms__meta-line">
              <strong>{t("inboeken.terms.date")}:</strong> {opts.date ? formatDate(opts.date, locale) : "—"}
            </p>
            <p className="fawkes-terms__meta-line">
              <strong>{t("inboeken.terms.place")}:</strong> {opts.place || "—"}
            </p>
            {opts.money && (
              <div className="fawkes-terms__meta-total">
                <span>{opts.money.label}</span>
                <strong>{opts.money.total}</strong>
              </div>
            )}
          </aside>
        </div>
        <div className="fawkes-terms__print">
          <Button variant="secondary" onClick={opts.onPrint} disabled={declPrintBezig}>
            <Printer size={16} aria-hidden="true" />
            {declPrintBezig ? t("common.loading") : t("inboeken.terms.print")}
          </Button>
          {declPrintError && (
            <span className="fawkes-inboeken__field-error" role="alert">{declPrintError}</span>
          )}
        </div>
      </div>
    );
  }

  function renderTermsAgreement(_klantId: number | null) {
    return renderDeclaration({
      header: t("inboeken.surrenderTerms.header"),
      info: t("inboeken.surrenderTerms.info"),
      contactTag: t("inboeken.terms.tag.eigenaar"),
      declTitle: t("inboeken.surrenderTerms.declTitle"),
      declIntro: t("inboeken.surrenderTerms.declIntro"),
      declText: t("inboeken.surrenderTerms.declText"),
      agreeLabel: t("inboeken.surrenderTerms.agree"),
      signLaterLabel: t("inboeken.surrenderTerms.signLater"),
      accepted: surrenderTerms.accepted,
      onAccepted: (v) => setSurrenderTerms((s) => ({ ...s, accepted: v })),
      signedLater: surrenderTerms.signedLater,
      onSignedLater: (v) => setSurrenderTerms((s) => ({ ...s, signedLater: v })),
      signature: surrenderTerms.signature,
      onSignature: (d) => setSurrenderTerms((s) => ({ ...s, signature: d })),
      date: surrenderTerms.date,
      onDate: (v) => setSurrenderTerms((s) => ({ ...s, date: v })),
      place: surrenderTerms.place,
      onPlace: (v) => setSurrenderTerms((s) => ({ ...s, place: v })),
      error: surrenderTermsBlocked
        ? (!surrenderTerms.accepted
            ? t("inboeken.surrenderTerms.agreeRequired")
            : t("inboeken.terms.signatureRequired"))
        : null,
      // RETURN shows what flows back instead of what is owed; the stray flow has no money at all.
      money: option === RETURN
        ? { label: t("inboeken.returnTerms.total"), total: formatEuro(returnRefund, locale), lines: [] }
        : {
            label: t("inboeken.costs.total"),
            total: formatEuro(surrenderCostTotal, locale),
            lines: surrenderCosts.map((c) => ({
              description: c.description,
              amount: formatEuro(parseAmount(c.amount) ?? 0, locale),
            })),
          },
      onPrint: () => doPrintDeclaration("AFSTANDSVERKLARING"),
    });
  }

  function renderSurrenderPayment() {
    // Het openstaande bedrag telt de factuurregel mee: wie alles op factuur zet, heeft niets meer
    // "openstaan" in deze stap — daar is dan een factuur voor.
    const remaining = surrenderCostTotal - surrenderPaymentPlanned;
    const due = formatAmount(surrenderCostTotal);
    return (
      <PaymentStep
        title={t("inboeken.payment.header")}
        info={t("inboeken.payment.info")}
        methods={SURRENDER_PAY_METHODS.map((m) => ({ ...m, label: t(m.labelKey) }))}
        selectedMethod={surrenderPayMethod}
        onPickMethod={(key) => {
          // De tegel is een korte weg naar de bedragregels: hij vult het volledige bedrag in bij de
          // gekozen methode. "Combinatie" maakt alles leeg zodat je zelf kunt verdelen.
          setSurrenderPayMethod(key as SurrenderPayMethod);
          setSurrenderPayment((sp) => ({
            ...sp,
            pin: key === "pin" ? due : "",
            cash: key === "cash" ? due : "",
            later: key === "invoice" ? due : "",
          }));
        }}
        rows={SURRENDER_PAY_ROWS.map((r) => ({ ...r, label: t(r.labelKey) }))}
        values={surrenderPayment}
        amountPlaceholder={t("inboeken.costs.amount.placeholder")}
        onAmountChange={(key, value) => setSurrenderPayment((sp) => ({ ...sp, [key]: value }))}
        remark={{
          label: t("inboeken.payment.invoiceRemark"),
          placeholder: t("inboeken.payment.invoiceRemark.placeholder"),
          value: surrenderPayment.reference,
          onChange: (value) => setSurrenderPayment((sp) => ({ ...sp, reference: value })),
        }}
        due={{ label: t("inboeken.payment.due"), amount: formatEuro(surrenderCostTotal, locale) }}
        paid={{ label: t("inboeken.payment.paid"), amount: formatEuro(surrenderPaidNow, locale) }}
        open={{ label: t("inboeken.payment.open"), amount: formatEuro(remaining, locale) }}
        remaining={{ label: t("inboeken.payment.remaining"), amount: formatEuro(remaining, locale) }}
        note={(parseAmount(surrenderPayment.later) ?? 0) > 0 ? t("inboeken.payment.invoiceNote") : undefined}
        error={surrenderTeveel
          ? t(surrenderOordeel.fout === "TEGENGESTELD" ? "betaling.tegengesteld" : "uitboeken.payment.teveel",
              { args: [formatEuro(Math.abs(remaining), locale)] })
          : undefined}
      />
    );
  }
  function renderReturnReasonSelect() {
    // Fixed "Reden retour" vocabulary (ReturnRestitutionFragment.reason.*); a legacy free-text value
    // (edit mode) that is not in the list stays selectable so an edit round-trip never wipes it.
    const extra =
      returnFields.reason && !RETURN_REASONS.includes(returnFields.reason) ? [returnFields.reason] : [];
    return (
      <SelectField
        label={t("inboeken.field.retourReden")}
        value={returnFields.reason}
        onChange={(e) => setReturnFields((s) => ({ ...s, reason: e.target.value }))}
        options={[
          { value: "", label: t("inboeken.field.retourReden.placeholder") },
          ...[...RETURN_REASONS, ...extra].map((r) => ({ value: r, label: r })),
        ]}
      />
    );
  }

  function renderReturnRestitution() {
    // Jmix ReturnRestitutionFragment: placement overview + paid adoption costs + refund choice.
    const placedOn = resti?.placedOn ?? null;
    const days = daysBetween(placedOn, intakeDatum);
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("inboeken.restitution.header")} info={t("inboeken.restitution.overview")} />
        <div className="fawkes-inboeken__resti-placement">
          <span>
            {t("inboeken.restitution.placedOn")} {placedOn ? formatDate(placedOn, locale) : "-"}
          </span>
          <span>
            {t("inboeken.restitution.returnOn")} {intakeDatum ? formatDate(intakeDatum, locale) : "-"}
          </span>
          <span>
            {t("inboeken.restitution.daysAtOwner")}{" "}
            <strong>{days == null ? "-" : `${days} ${t("inboeken.restitution.daysSuffix")}`}</strong>
          </span>
        </div>
        <div className="fawkes-inboeken__resti-costs">
          <span className="fui-field__label">{t("inboeken.restitution.costsTitle")}</span>
          <div className="fawkes-inboeken__resti-row fawkes-inboeken__resti-row--head">
            <span>{t("inboeken.restitution.costsDescription")}</span>
            <span>{t("inboeken.restitution.costsAmount")}</span>
          </div>
          {(resti?.lines ?? []).map((line, i) => (
            <div className="fawkes-inboeken__resti-row" key={i}>
              <span>{line.description ?? "-"}</span>
              <span>{formatEuro(line.amount, locale)}</span>
            </div>
          ))}
          {(resti?.lines ?? []).length === 0 && (
            <div className="fawkes-inboeken__resti-row">
              <span className="fawkes-inboeken__muted">-</span>
              <span />
            </div>
          )}
          <div className="fawkes-inboeken__resti-row fawkes-inboeken__resti-row--total">
            <span>{t("inboeken.restitution.totalPaid")}</span>
            <strong>{formatEuro(resti?.totalPaid ?? 0, locale)}</strong>
          </div>
        </div>
        {resti?.invoiceKlantId != null && relationId != null && relationId !== resti.invoiceKlantId && (
          <div className="fawkes-inboeken__notice fawkes-inboeken__notice--invalid" role="alert">
            {t("inboeken.restitution.ownerMismatch")}
          </div>
        )}
        <div className="fawkes-inboeken__grid">
          {renderReturnReasonSelect()}
          <Field label={t("inboeken.field.opmerking")} value={returnFields.remark}
            placeholder={t("inboeken.restitution.remarkPlaceholder")}
            onChange={(e) => setReturnFields((s) => ({ ...s, remark: e.target.value }))} />
        </div>
        <span className="fui-field__label">{t("inboeken.restitution.info")}</span>
        <div className="fawkes-inboeken__choice">
          <button
            type="button"
            className={returnRestitution.selected ? "is-selected" : ""}
            onClick={() => setReturnRestitution((s) => ({ ...s, selected: true }))}
          >
            {t("inboeken.restitution.yes")}
          </button>
          <button
            type="button"
            className={!returnRestitution.selected ? "is-selected" : ""}
            onClick={() => setReturnRestitution({ selected: false, amount: "" })}
          >
            {t("inboeken.restitution.no")}
          </button>
        </div>
        {returnRestitution.selected && (
          <div className="fawkes-inboeken__field">
            <Field label={t("inboeken.restitution.amount")} type="text" inputMode="decimal" value={returnRestitution.amount}
              placeholder={t("inboeken.costs.amount.placeholder")}
              onChange={(e) => setReturnRestitution((s) => ({ ...s, amount: e.target.value }))}
              onBlur={() => {
                // Jmix clamps the NumberField to [0, max] — normalize on leave.
                const parsed = parseAmount(returnRestitution.amount);
                if (parsed == null) return;
                const clamped = Math.min(Math.max(parsed, 0), returnMaxRefund);
                if (clamped !== parsed) {
                  setReturnRestitution((s) => ({ ...s, amount: clamped.toFixed(2) }));
                }
              }} />
            <span className="fawkes-inboeken__helper">
              {t("inboeken.restitution.max")} {formatEuro(returnMaxRefund, locale)}
            </span>
          </div>
        )}
      </div>
    );
  }

  function renderReturnPayment() {
    const remaining = Math.max(0, returnRefund - returnPaidOut);
    const full = formatAmount(returnRefund);
    // Een terugstorting kan alleen naar een rekening, dus hangt het IBAN-veld onder die regel —
    // zichtbaar zodra er een bedrag op staat, met dezelfde blokkerende meldingen als voorheen.
    const ibanExtra = returnPayTransfer > 0 ? (
      <div className="fawkes-inboeken__field">
        <Field
          label={t("inboeken.returnPayment.iban")}
          value={returnPay.iban}
          placeholder={t("inboeken.returnPayment.ibanPlaceholder")}
          onChange={(e) => setReturnPay((rp) => ({ ...rp, iban: e.target.value }))}
        />
        {!returnPay.iban.trim() ? (
          <span className="fawkes-inboeken__field-error">{t("inboeken.returnPayment.ibanRequired")}</span>
        ) : !isValidIban(returnPay.iban) ? (
          <span className="fawkes-inboeken__field-error">{t("inboeken.returnPayment.ibanInvalid")}</span>
        ) : null}
      </div>
    ) : undefined;
    return (
      <PaymentStep
        title={t("inboeken.returnPayment.header")}
        info={t("inboeken.returnPayment.info")}
        methods={RETURN_PAY_TILES.map((m) => ({ ...m, label: t(m.labelKey) }))}
        selectedMethod={returnPayMethod}
        onPickMethod={(key) => {
          setReturnPayMethod(key as ReturnPayTile);
          setReturnPay((rp) => ({
            ...rp,
            transfer: key === "transfer" ? full : "",
            pin: key === "pin" ? full : "",
            cash: key === "cash" ? full : "",
          }));
        }}
        rows={RETURN_PAY_METHODS.map((r) => ({
          ...r,
          label: t(r.labelKey),
          extra: r.key === "transfer" ? ibanExtra : undefined,
        }))}
        values={returnPay}
        amountPlaceholder={t("inboeken.costs.amount.placeholder")}
        onAmountChange={(key, value) => setReturnPay((rp) => ({ ...rp, [key]: value }))}
        remark={{
          label: t("inboeken.returnPayment.remark"),
          placeholder: t("inboeken.returnPayment.remarkPlaceholder"),
          value: returnPay.remark,
          onChange: (value) => setReturnPay((rp) => ({ ...rp, remark: value })),
        }}
        due={{ label: t("inboeken.returnPayment.toRefund"), amount: formatEuro(returnRefund, locale) }}
        paid={{ label: t("inboeken.returnPayment.paidOut"), amount: formatEuro(returnPaidOut, locale) }}
        open={{ label: t("inboeken.returnPayment.open"), amount: formatEuro(remaining, locale) }}
        remaining={{ label: t("inboeken.returnPayment.stillToPay"), amount: formatEuro(remaining, locale) }}
      />
    );
  }
  function renderChipStep() {
    // PetBase unreachable: the form is hidden, the operator is told registration is skipped, and may
    // continue (Jmix markPetbaseUnreachable). buildValues sends no captures, so no rows are enqueued.
    if (chipOutage) {
      return (
        <div className="fawkes-chipreg fawkes-chipreg--outage">
          <div className="fawkes-chipreg__notice" role="alert">{t("inboeken.chip.outage")}</div>
        </div>
      );
    }
    return (
      <div className="fawkes-chipreg">
        <StepIntro title={t("inboeken.chip.header")} info={t("inboeken.chip.info")} />
        {chippedAnimals.map(({ sel }) => {
          const d = chipDraft[sel.id];
          if (!d) return null;
          const errors = chipCaptureErrors(d);
          const isGeboorte = d.aanvoerType === AANVOER_GEBOORTE;
          const isNieuweChip = d.aanvoerType === AANVOER_NIEUWE_CHIP;
          return (
            <div key={sel.id} className="fawkes-chipreg__card">
              <span className="fawkes-chipreg__animal">{sel.label}</span>
              <ChipAnimalReadonly animalId={sel.id} t={t} />
              <div className="fawkes-chipreg__grid">
                <SelectField
                  label={t("inboeken.chip.field.aanvoerType")}
                  value={d.aanvoerType}
                  onChange={(e) => setChip(sel.id, { aanvoerType: e.target.value })}
                  options={AANVOER_TYPES.map((v) => ({ value: v, label: t(`inboeken.chip.aanvoerType.${v}`) }))}
                />
                <Field
                  label={t("inboeken.chip.field.eventDate")}
                  type="date"
                  value={d.eventDate}
                  onChange={(e) => setChip(sel.id, { eventDate: e.target.value })}
                />
                <Field
                  label={t("inboeken.chip.field.chipNumber")}
                  value={d.chipNumber}
                  placeholder={t("inboeken.chip.field.chipNumber.placeholder")}
                  onChange={(e) => setChip(sel.id, { chipNumber: e.target.value })}
                />
                <Field
                  label={t("inboeken.chip.field.countryOfOrigin")}
                  value={d.countryOfOrigin}
                  onChange={(e) => setChip(sel.id, { countryOfOrigin: e.target.value })}
                />
                {isGeboorte && (
                  <Field
                    label={t("inboeken.chip.field.motherChip")}
                    value={d.motherChipNumber}
                    placeholder={t("inboeken.chip.field.motherChip.placeholder")}
                    onChange={(e) => setChip(sel.id, { motherChipNumber: e.target.value })}
                  />
                )}
                {isNieuweChip && (
                  <Field
                    label={t("inboeken.chip.field.chipInsertDate")}
                    type="date"
                    value={d.newChipInsertOn}
                    onChange={(e) => setChip(sel.id, { newChipInsertOn: e.target.value })}
                  />
                )}
                <ComboBox
                  label={t("inboeken.chip.field.chipper")}
                  value={d.chipperCibgNumber}
                  onChange={(v) => setChip(sel.id, { chipperCibgNumber: v })}
                  placeholder={t("inboeken.chip.field.chipper.placeholder")}
                  options={(chipIdentifiers?.identifiers ?? []).map((o) => ({
                    value: o.cibgNumber,
                    label: o.displayName,
                  }))}
                />
                <Field
                  label={t("inboeken.chip.field.chipReadDate")}
                  type="date"
                  value={d.chipReadDate}
                  onChange={(e) => setChip(sel.id, { chipReadDate: e.target.value })}
                />
              </div>
              {errors.length > 0 && (
                <ul className="fawkes-chipreg__errors">
                  {errors.map((k) => (
                    <li key={k} className="fawkes-inboeken__field-error">{t(k)}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── per-flow rich summaries (audit §3 row 21 / §7 slice 9) ─────────────────────────────────────
  // Mirrors the Jmix summary fragments: Stray/generic = StrayShelterIntakeSummaryFragment /
  // ShelterIntakeFlowSummaryFragment (Registratie-card met foto + tegels + kaart-echo + vinder-echo),
  // SURRENDER/RETURN = Surrender-/ReturnShelterIntakeSummaryFragment (Dier-card met foto + 6 tegels
  // incl. verklaring-checkmarks, chip-notitie, betaling en de print-knop).

  /** The Jmix summary animal card: labeled facts left, photo box right — one row per animal. */
  function renderSummaryAnimals(sectionTitle: string, withCoat: boolean) {
    return (
      <SummaryTile title={sectionTitle} full>
        {selected.map((a) => (
          <div key={a.id} className="fawkes-inboeken__summary-animal-row">
            <div className="fawkes-inboeken__summary-animal-facts">
              <SummaryRow label={t("inboeken.summary.label.naam")} value={a.naam || "—"} />
              <SummaryRow label={t("inboeken.summary.label.chip")} value={a.chip || "—"} />
              <SummaryRow
                label={t(withCoat ? "inboeken.summary.label.type" : "inboeken.summary.label.diersoortRas")}
                value={[a.species, a.ras].filter(Boolean).join(" - ") || "—"}
              />
              <SummaryRow label={t("inboeken.summary.label.geslacht")} value={a.geslacht || "—"} />
              {withCoat && (
                <SummaryRow
                  label={t("inboeken.summary.label.vachtKleur")}
                  value={[a.vacht, a.kleur].filter(Boolean).join(" - ") || "—"}
                />
              )}
            </div>
            <SummaryPhotoBox fotoUrl={photoSrc(a)} name={a.naam} noPhotoLabel={t("inboeken.results.noPhoto")} />
          </div>
        ))}
        {selected.length === 0 && <SummaryRow label="" value="—" />}
      </SummaryTile>
    );
  }

  /** The verklaring-echo tile (Jmix ✓/• check rows + datum/plaats), shared by SURRENDER and RETURN. */
  function renderSummaryTerms() {
    const acceptedLabel = surrenderTerms.accepted
      ? t(surrenderTerms.signedLater && !surrenderTerms.signature ? "inboeken.summary.terms.acceptedLater" : "inboeken.summary.terms.accepted")
      : t("inboeken.summary.terms.notAccepted");
    return (
      <SummaryTile title={t("inboeken.summary.section.verklaring")}>
        <CheckRow ok={surrenderTerms.accepted} label={acceptedLabel} />
        <CheckRow
          ok={!!surrenderTerms.signature}
          label={t(surrenderTerms.signature ? "inboeken.summary.terms.signed" : "inboeken.summary.terms.notSigned")}
        />
        <SummaryRow label={t("inboeken.terms.date")} value={surrenderTerms.date ? formatDate(surrenderTerms.date, locale) : "—"} />
        <SummaryRow label={t("inboeken.terms.place")} value={surrenderTerms.place || "—"} />
      </SummaryTile>
    );
  }

  /** The betaling-echo tile: paid = amount + methods, unpaid = the red "Niet voldaan" line (Jmix). */
  function renderSummaryPayment(paidNow: number, methods: string) {
    return (
      <SummaryTile title={t("inboeken.summary.section.betaling")}>
        {paidNow > 0 ? (
          <>
            <SummaryRow label={t("inboeken.summary.label.paid")} value={formatEuro(paidNow, locale)} />
            <SummaryRow label={t("inboeken.summary.label.paymentMethod")} value={methods || "—"} />
          </>
        ) : (
          <span className="fawkes-inboeken__line--not-paid">{t("inboeken.summary.notPaid")}</span>
        )}
      </SummaryTile>
    );
  }

  function renderSummary() {
    const soortLabel = soorten.find((s) => String(s.id) === soortId)?.label ?? "—";
    const gemeenteLabel = gemeenten.find((g) => String(g.id) === gemeenteId)?.label ?? "—";
    const titleKey = option === RETURN ? "inboeken.summary.title.return" : "inboeken.summary.title";
    const infoKey =
      option === SURRENDER ? "inboeken.summary.info.surrender"
      : option === RETURN ? "inboeken.summary.info.return"
      : "inboeken.summary.info";
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t(titleKey)} info={t(infoKey)} />
        <div className="fawkes-inboeken__summary-grid">
          {option === STRAY && renderStraySummary(soortLabel, gemeenteLabel)}
          {option === SURRENDER && renderSurrenderSummary(soortLabel)}
          {option === RETURN && renderReturnSummary(soortLabel)}
          {(option === BIRTH || option === EXCHANGE || option === "DEFAULT") &&
            renderGenericSummary(soortLabel, gemeenteLabel)}
        </div>
        {(option === SURRENDER || option === RETURN) && (
          <span className="fawkes-inboeken__summary-footer-note">{t("inboeken.summary.footerNote")}</span>
        )}
      </div>
    );
  }

  // Jmix StrayShelterIntakeSummaryFragment: Registratie-card + Binnenkomst/Vindplaats/Dierenambulance
  // + Kaart-echo (read-only) + Vinder ("Ondertekend op … door …") + "Print vindersverklaring".
  function renderStraySummary(soortLabel: string, gemeenteLabel: string) {
    const ambulanceLabel = dierenambulances.find((d) => String(d.id) === ambulance.id)?.label ?? null;
    const streetLine =
      [stray.street, [stray.houseNumber, stray.houseNumberAddition].filter(Boolean).join("")]
        .filter(Boolean)
        .join(" ");
    const postalCity = [stray.postalCode, stray.city].filter(Boolean).join(" ");
    const finderName = termsKlant?.naam ?? finderLabel;
    return (
      <>
        {renderSummaryAnimals(t("inboeken.summary.section.registratie"), true)}
        <SummaryTile title={t("inboeken.summary.section.binnenkomst")}>
          <SummaryRow label={t("inboeken.summary.label.type")} value={soortLabel} />
          <SummaryRow label={t("inboeken.field.datum")} value={intakeDatum ? formatDate(intakeDatum, locale) : "—"} />
        </SummaryTile>
        <SummaryTile title={t("inboeken.summary.vindplaats")}>
          <SummaryRow label={t("inboeken.summary.label.straat")} value={streetLine || "—"} />
          <SummaryRow label={t("inboeken.summary.label.postcodePlaats")} value={postalCity || "—"} />
          <SummaryRow label={t("inboeken.field.gemeente")} value={gemeenteLabel} />
          <SummaryRow label={t("inboeken.field.opmerking")} value={stray.remark || "—"} />
          <SummaryRow
            label={t("inboeken.field.gedumpt")}
            value={stray.dumped ? t("inboeken.gedumpt.ja") : t("inboeken.gedumpt.nee")}
          />
        </SummaryTile>
        <SummaryTile title={t("inboeken.field.dierenambulance")}>
          <SummaryRow label="" value={(ambulance.yes ? ambulanceLabel : null) || "—"} />
          <SummaryRow label={t("inboeken.summary.label.ritnummer")} value={(ambulance.yes ? ambulance.ride : "") || "—"} />
        </SummaryTile>
        {/* Two columns wide so the map is legible and the last row fills out beside the Vinder tile. */}
        <SummaryTile title={t("inboeken.map.label")} wide>
          {/* Kaart-echo van de vindplaats (Jmix stray-map-canvas--readonly): pointer-events uit. */}
          <div className="fawkes-inboeken__map-echo">
            <FoundLocationMap lat={carry.lat} lng={carry.lng} onPick={() => undefined} />
          </div>
        </SummaryTile>
        <SummaryTile title={t("inboeken.summary.section.vinder")}>
          {finderId != null ? (
            <>
              {strayTerms.accepted && (
                <span className="fawkes-inboeken__summary-signed">
                  {t("inboeken.summary.signedOn", {
                    args: [strayTerms.date ? formatDate(strayTerms.date, locale) : "—", finderName ?? "—"],
                  })}
                </span>
              )}
              <SummaryRow label={t("inboeken.summary.label.naam")} value={finderName || "—"} />
              <SummaryRow label={t("inboeken.summary.adres")} value={addressLine(termsKlant) || "—"} />
              <SummaryRow
                label={t("inboeken.summary.label.telefoon")}
                value={[termsKlant?.telefoon, termsKlant?.mobiel].filter(Boolean).join(" / ") || "—"}
              />
            </>
          ) : (
            <span className="fawkes-inboeken__muted">{t("inboeken.summary.finder.none")}</span>
          )}
        </SummaryTile>
        {/* Printing is an action, not a fact — a whole empty tile in the grid for it left a hole.
            It sits as a small button under the tiles instead. */}
        {finderId != null && (
          <div className="fawkes-inboeken__summary-actions">
            <button type="button" className="fawkes-inboeken__link" onClick={() => doPrintDeclaration("VINDERSVERKLARING")}>
              {t("inboeken.summary.printStray")}
            </button>
          </div>
        )}
      </>
    );
  }

  // Jmix SurrenderShelterIntakeSummaryFragment: Dier-card + Binnenkomst/Eigenaar/Kosten/
  // Afstandsverklaring/Chipregistratie/Betaling + "Print afstandsverklaring".
  function renderSurrenderSummary(soortLabel: string) {
    const methods = [
      (parseAmount(surrenderPayment.pin) ?? 0) > 0 ? t("inboeken.payment.pin") : null,
      (parseAmount(surrenderPayment.cash) ?? 0) > 0 ? t("inboeken.payment.cash") : null,
    ]
      .filter(Boolean)
      .join(", ");
    return (
      <>
        {renderSummaryAnimals(t("inboeken.summary.section.dier"), false)}
        <SummaryTile title={t("inboeken.summary.section.binnenkomst")}>
          <SummaryRow label={t("inboeken.summary.label.type")} value={soortLabel} />
          <SummaryRow label={t("inboeken.field.redenAfstand")} value={surrender.reason || "—"} />
          {surrender.remark && <SummaryRow label={t("inboeken.field.opmerking")} value={surrender.remark} />}
        </SummaryTile>
        <SummaryTile title={t("inboeken.field.eigenaar")}>
          <SummaryRow label={t("inboeken.summary.label.naam")} value={termsKlant?.naam ?? relationName ?? "—"} />
          <SummaryRow label={t("inboeken.summary.adres")} value={addressLine(termsKlant) || "—"} />
          <SummaryRow label={t("inboeken.summary.label.email")} value={termsKlant?.email || "—"} />
        </SummaryTile>
        <SummaryTile title={t("inboeken.step.costs")}>
          {surrenderCosts.map((c) => (
            <div key={c.id} className="fawkes-inboeken__summary-cost-row">
              <span>
                {c.description || "—"}
                {/* De korting staat bij de regel waar hij op zit; anders lees je honderd euro kosten
                    met vijfenzeventig euro totaal eronder en klopt er iets niet dat wel klopt. */}
                {kortingVanRegel(c) > 0 && (
                  <small className="fawkes-inboeken__summary-korting">
                    {t("korting.label")} -&nbsp;{formatEuro(kortingVanRegel(c), locale)}
                  </small>
                )}
              </span>
              <span>{formatEuro(regelNaKorting(c), locale)}</span>
            </div>
          ))}
          {surrenderCosts.length === 0 && (
            <span className="fawkes-inboeken__muted">{t("inboeken.costs.empty")}</span>
          )}
          <div className="fawkes-inboeken__summary-cost-row fawkes-inboeken__summary-cost-row--total">
            <span>{t("inboeken.summary.label.totaal")}</span>
            <span>{formatEuro(surrenderCostTotal, locale)}</span>
          </div>
        </SummaryTile>
        {renderSummaryTerms()}
        {showChipStep && (
          <SummaryTile title={t("inboeken.summary.section.chip")}>
            {chippedAnimals.length > 0 ? (
              <span className="fawkes-inboeken__summary-chip-note">
                {t("inboeken.summary.chip.update")}:{" "}
                {chippedAnimals.map(({ sel }) => chipDraft[sel.id]?.chipNumber || sel.chip).filter(Boolean).join(", ")}
              </span>
            ) : (
              <span className="fawkes-inboeken__muted">{t("inboeken.summary.chip.none")}</span>
            )}
          </SummaryTile>
        )}
        {renderSummaryPayment(surrenderPaidNow, methods)}
        <div className="fawkes-inboeken__summary-tile fawkes-inboeken__summary-print">
          <button type="button" className="fawkes-inboeken__link" onClick={() => doPrintDeclaration("AFSTANDSVERKLARING")}>
            {t("inboeken.summary.printSurrender")}
          </button>
        </div>
      </>
    );
  }

  // Jmix ReturnShelterIntakeSummaryFragment: Dier-card + Binnenkomst (geplaatst/retour/duur) /
  // Eigenaar / Kosten & restitutie / Chipregistratie / Betaling / Afstandsverklaring + print.
  function renderReturnSummary(soortLabel: string) {
    const placedOn = resti?.placedOn ?? null;
    const stayDays = daysBetween(placedOn, intakeDatum);
    const chipCostTotal = (resti?.lines ?? [])
      .filter((l) => (l.description ?? "").toLowerCase().includes("chip"))
      .reduce((sum, l) => sum + l.amount, 0);
    const totalPaid = resti?.totalPaid ?? 0;
    return (
      <>
        {renderSummaryAnimals(t("inboeken.summary.section.dier"), false)}
        <SummaryTile title={t("inboeken.summary.section.binnenkomst")}>
          <SummaryRow label={t("inboeken.summary.label.intakeType")} value={soortLabel} />
          <SummaryRow
            label={t("inboeken.summary.label.placedOn")}
            value={placedOn ? formatDate(placedOn, locale) : "—"}
          />
          <SummaryRow
            label={t("inboeken.summary.label.returnedOn")}
            value={intakeDatum ? formatDate(intakeDatum, locale) : "—"}
          />
          <SummaryRow
            label={t("inboeken.summary.label.duration")}
            value={stayDays != null ? `${stayDays} ${t("inboeken.summary.days")}` : "—"}
          />
        </SummaryTile>
        <SummaryTile title={t("inboeken.field.eigenaar")}>
          <SummaryRow label={t("inboeken.summary.label.naam")} value={termsKlant?.naam ?? relationName ?? "—"} />
          <SummaryRow label={t("inboeken.summary.adres")} value={addressLine(termsKlant) || "—"} />
          <SummaryRow label={t("inboeken.summary.label.email")} value={termsKlant?.email || "—"} />
          <SummaryRow
            label={t("inboeken.summary.label.telefoon")}
            value={[termsKlant?.telefoon, termsKlant?.mobiel].filter(Boolean).join(" / ") || "—"}
          />
        </SummaryTile>
        <SummaryTile title={t("inboeken.summary.section.kostenRestitutie")}>
          <SummaryRow label={t("inboeken.summary.label.totalPaid")} value={formatEuro(totalPaid, locale)} />
          <SummaryRow
            label={t("inboeken.summary.label.adoptionCosts")}
            value={formatEuro(totalPaid - chipCostTotal, locale)}
          />
          <SummaryRow label={t("inboeken.summary.label.chipCosts")} value={formatEuro(chipCostTotal, locale)} />
          <SummaryRow label={t("inboeken.summary.label.refunded")} value={formatEuro(returnRefund, locale)} />
          <SummaryRow
            label={t("inboeken.summary.label.restitutionCompleted")}
            value={t(returnRestitution.selected ? "inboeken.summary.value.yes" : "inboeken.summary.value.no")}
          />
        </SummaryTile>
        {showChipStep && (
          <SummaryTile title={t("inboeken.summary.section.chip")}>
            <span className="fawkes-inboeken__summary-chip-note">{t("inboeken.summary.chip.infoReturn")}</span>
          </SummaryTile>
        )}
        {showReturnPayment && (
          <SummaryTile title={t("inboeken.summary.section.betaling")}>
            <SummaryRow label={t("inboeken.summary.label.paymentMethod")} value={returnPayMethodSummary || "—"} />
            <SummaryRow label={t("inboeken.summary.label.bedrag")} value={formatEuro(returnPaidOut, locale)} />
            <SummaryRow
              label={t("inboeken.summary.label.rekening")}
              value={returnPayTransfer > 0 ? returnPay.iban.trim() || "—" : "—"}
            />
          </SummaryTile>
        )}
        {renderSummaryTerms()}
        <div className="fawkes-inboeken__summary-tile fawkes-inboeken__summary-print">
          <button type="button" className="fawkes-inboeken__link" onClick={() => doPrintDeclaration("AFSTANDSVERKLARING")}>
            {t("inboeken.summary.printReturn")}
          </button>
        </div>
      </>
    );
  }

  // Jmix ShelterIntakeFlowSummaryFragment (BIRTH / EXCHANGE / generic): Registratie-card +
  // Binnenkomst + Geboorte-tegel (birth) of Relatie-tegel (rol Brenger/Vinder; hidden for birth).
  function renderGenericSummary(soortLabel: string, gemeenteLabel: string) {
    return (
      <>
        {renderSummaryAnimals(t("inboeken.summary.section.registratie"), true)}
        <SummaryTile title={t("inboeken.summary.section.binnenkomst")}>
          <SummaryRow label={t("inboeken.summary.label.type")} value={soortLabel} />
          <SummaryRow label={t("inboeken.field.datum")} value={intakeDatum ? formatDate(intakeDatum, locale) : "—"} />
        </SummaryTile>
        {option === BIRTH ? (
          <SummaryTile title={t("inboeken.summary.geboorte")}>
            <SummaryRow
              label={t("inboeken.summary.label.geboortedatum")}
              // BIRTH: the intake date IS the geboortedatum (set equal on the details step).
              value={intakeDatum ? formatDate(intakeDatum, locale) : "—"}
            />
            <SummaryRow label={t("inboeken.field.moederdier")} value={birth.motherName || "—"} />
            <SummaryRow label={t("inboeken.field.gemeente")} value={gemeenteLabel} />
            {birth.remark && <SummaryRow label={t("inboeken.field.opmerking")} value={birth.remark} />}
          </SummaryTile>
        ) : (
          <SummaryTile title={t("inboeken.summary.section.relatie")}>
            <SummaryRow
              label={t("inboeken.summary.label.rol")}
              value={
                relationId == null
                  ? "—"
                  : t(relationViaBringer ? "inboeken.summary.rol.brenger" : "inboeken.summary.rol.vinder")
              }
            />
            <SummaryRow label={t("inboeken.summary.label.naam")} value={termsKlant?.naam ?? relationName ?? "—"} />
            <SummaryRow label={t("inboeken.summary.adres")} value={addressLine(termsKlant) || "—"} />
            <SummaryRow
              label={t("inboeken.summary.label.telefoon")}
              value={[termsKlant?.telefoon, termsKlant?.mobiel].filter(Boolean).join(" / ") || "—"}
            />
          </SummaryTile>
        )}
      </>
    );
  }
}

function SummaryTile({ title, full, wide, children }: {
  title: string; full?: boolean; wide?: boolean; children: ReactNode;
}) {
  return (
    <div
      className={
        "fawkes-inboeken__summary-tile"
        + (full ? " fawkes-inboeken__summary-tile--full" : "")
        + (wide ? " fawkes-inboeken__summary-tile--wide" : "")
      }
    >
      <span className="fawkes-inboeken__summary-tile-title">{title}</span>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="fawkes-inboeken__summary-row">
      {label && <span className="fui-field__label">{label}</span>}
      <span>{value}</span>
    </div>
  );
}

/** The Jmix ✓/• check row (verklaring-echo): green check when done, muted bullet when not. */
function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="fawkes-inboeken__check-row">
      <span
        className={"fawkes-inboeken__check-icon" + (ok ? "" : " fawkes-inboeken__check-icon--off")}
        aria-hidden="true"
      >
        {ok ? "✓" : "•"}
      </span>
      <span>{label}</span>
    </div>
  );
}

/** The Jmix summary photo box: dierfoto (camera-fallback) with the animal's name underneath. */
function SummaryPhotoBox({ fotoUrl, name, noPhotoLabel }: { fotoUrl: string | null; name: string; noPhotoLabel: string }) {
  return (
    <div className="fawkes-inboeken__summary-photo-box">
      {fotoUrl ? (
        <img className="fawkes-inboeken__summary-photo" src={fotoUrl} alt={name} />
      ) : (
        <span
          className="fawkes-inboeken__summary-photo fawkes-inboeken__summary-photo--empty"
          title={noPhotoLabel}
          aria-label={noPhotoLabel}
        />
      )}
      <span className="fawkes-inboeken__summary-photo-name">{name || "—"}</span>
    </div>
  );
}

function animalLabel(a: SubwizardAnimal): string {
  const chip = a.chipPasnummer ? ` · ${a.chipPasnummer}` : "";
  const ras = a.ras ? ` (${a.ras})` : "";
  return `${a.naam}${ras}${chip}`;
}
